import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import Lottie from 'lottie-react';
import { Pencil } from 'lucide-react';
import Button from '../../components/Button';
import Card from '../../components/Card';
import TeamMembersCard from '../../components/dashboard/TeamMembersCard';
import InviteTeammateModal from '../../components/modals/InviteTeammateModal';
import PortalAvatar from '../../components/profile/PortalAvatar';
import Skeleton from '../../components/ui/Skeleton';
import { useAuth } from '../../context/AuthProvider';
import { apiFetch } from '../../lib/api';
import {
  fetchBillingPaymentMethods,
  fetchBillingSubscription,
  invalidateBillingCache,
} from '../../lib/clientBilling';
import {
  formatOrgInviteRoleLabel,
  getOrgInviteNotificationMeta,
  type OrgInviteNotificationMeta,
} from '../../lib/orgInvites';
import { gradientPillCss } from '../../styles/gradientPill';
import { formatUsPhone, isValidUsPhone } from '../../lib/usPhone';
import confettiAnimation from '../../assets/confetti.json';
import circuitAnimation from '../../assets/circuit.json';
import {
  fetchServiceWorkspaceCatalog,
  fetchServiceWorkspaceSummary,
  transferCurrentClientServiceToOrganization,
  type ServiceCatalogService,
  type ServiceCatalogTier,
  type ServiceWorkspaceCurrentService,
  type ServiceWorkspaceProposal,
} from '../../lib/clientServices';
import { storage } from '../../lib/firebase';
import {
  type ClientOrgBusinessProfile,
  type ClientOrgMember,
  deleteClientOrganization,
  fetchClientOrgBusinessProfile,
  fetchClientOrgMembers,
  leaveClientOrganization,
  transferClientOrgPrimaryAccountHolder,
  updateClientOrgBusinessProfile,
} from '../../lib/clientOrganization';
import type { PaymentMethod } from '../../store/features/billing/types';

type InvitePreviewMember = {
  uid: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  photoURL: string | null;
  role: 'owner' | 'member';
  isPrimaryAccountHolder: boolean;
};

type InvitePreview = {
  orgId: string;
  invitedEmail: string | null;
  invitedRole: 'owner' | 'member';
  businessName: string;
  organizationType: 'individual' | 'business';
  logoURL: string | null;
  invitedByName: string | null;
  invitedByPhotoURL: string | null;
  primaryAccountHolderName: string;
  industry: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  website: string | null;
  address: string | null;
  members: InvitePreviewMember[];
};

type InviteBannerConfettiPreset = {
  top: string;
  left: string;
  size: number;
  rotate: number;
  scale: number;
  opacity: number;
};

const INVITE_BANNER_CONFETTI_SIZE_MULTIPLIER = 2;

const INVITE_BANNER_CONFETTI_PRESETS: InviteBannerConfettiPreset[] = [
  { top: '18%', left: '10%', size: 126, rotate: -16, scale: 0.96, opacity: 0.84 },
  { top: '16%', left: '34%', size: 130, rotate: 12, scale: 1.02, opacity: 0.9 },
  { top: '28%', left: '58%', size: 118, rotate: -10, scale: 0.92, opacity: 0.82 },
  { top: '24%', left: '80%', size: 124, rotate: 18, scale: 1.04, opacity: 0.88 },
  { top: '68%', left: '14%', size: 120, rotate: -8, scale: 0.94, opacity: 0.8 },
  { top: '70%', left: '38%', size: 134, rotate: 14, scale: 1.08, opacity: 0.9 },
  { top: '64%', left: '62%', size: 124, rotate: -14, scale: 0.98, opacity: 0.84 },
  { top: '72%', left: '86%', size: 116, rotate: 10, scale: 0.9, opacity: 0.8 },
];

function pickInviteBannerConfettiPreset(
  excludeIndex?: number,
): { index: number; preset: InviteBannerConfettiPreset } {
  if (INVITE_BANNER_CONFETTI_PRESETS.length === 1) {
    return { index: 0, preset: INVITE_BANNER_CONFETTI_PRESETS[0] };
  }

  let index = Math.floor(Math.random() * INVITE_BANNER_CONFETTI_PRESETS.length);
  while (index === excludeIndex) {
    index = Math.floor(Math.random() * INVITE_BANNER_CONFETTI_PRESETS.length);
  }
  return {
    index,
    preset: INVITE_BANNER_CONFETTI_PRESETS[index],
  };
}

const Wrap = styled.div`
  display: grid;
  place-items: start center;
  padding: 2rem 1rem;
`;

const PageShell = styled.div`
  width: 100%;
  max-width: 1220px;
`;

const Section = styled.section`
  margin-top: 1.45rem;
`;

const OrgSwitcherBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .8rem;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const OrgSwitcherMeta = styled.div`
  display: grid;
  gap: .18rem;
  min-width: 0;

  @media (max-width: 900px) {
    justify-items: center;
    text-align: center;
  }
`;

const OrgSwitcherLabel = styled.span`
  color: #9aa4b2;
  font-size: .8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
`;

const OrgSwitcherValue = styled.strong`
  color: #e8eaed;
  font-weight: 900;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 900px) {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }
`;

const OrgSwitcherSelect = styled.select`
  min-width: 220px;
  max-width: 45%;
  padding: .58rem .66rem;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.14);
  background: rgba(255,255,255,.04);
  color: #e8eaed;

  @media (max-width: 900px) {
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }
`;

const InviteBannerStack = styled.div`
  display: grid;
  gap: .8rem;
  margin-top: 1rem;
`;

const InviteBanner = styled(Card)`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: .8rem 1rem;
  align-items: center;
  background: #e0a15e;
  border: 0;
  box-shadow: none;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const InviteBannerConfettiLayer = styled.div<{
  $top: string;
  $left: string;
  $size: number;
  $rotate: number;
  $scale: number;
  $opacity: number;
}>`
  position: absolute;
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
  width: ${({ $size }) => `${Math.round($size * INVITE_BANNER_CONFETTI_SIZE_MULTIPLIER)}px`};
  height: ${({ $size }) => `${Math.round($size * INVITE_BANNER_CONFETTI_SIZE_MULTIPLIER)}px`};
  transform: translate(-50%, -50%) rotate(${({ $rotate }) => $rotate}deg) scale(${({ $scale }) => $scale});
  opacity: ${({ $opacity }) => $opacity};
  pointer-events: none;
  z-index: 0;
  filter: saturate(1.08) drop-shadow(0 8px 18px rgba(35, 24, 21, 0.12));
`;

const InviteBannerText = styled.div`
  display: grid;
  gap: .3rem;
  min-width: 0;
  position: relative;
  z-index: 1;
`;

const InviteBannerTitle = styled.strong`
  color: #ffffff;
  font-weight: 900;
  font-size: clamp(1.05rem, 1.5vw, 1.2rem);
  line-height: 1.35;
`;

const InviteBannerCopy = styled.p`
  margin: 0;
  color: rgba(35, 24, 21, .82);
  line-height: 1.55;
`;

const InviteBannerActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: .65rem;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 720px) {
    justify-content: flex-start;
  }
`;

const InviteBannerButton = styled(Button)`
  background: transparent;
  border: 1px dashed rgba(35, 24, 21, 0.56);
  color: #231815;
  box-shadow: none;

  &:hover {
    background: rgba(35, 24, 21, 0.08);
    border-color: rgba(35, 24, 21, 0.76);
    color: #231815;
    transform: none;
  }
`;

const OrganizationTeamPanel = styled(TeamMembersCard)`
  min-height: 320px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 .45rem;
  font-size: clamp(1.25rem, 2vw, 1.6rem);
  line-height: .96;
  letter-spacing: -.03em;
  font-weight: 900;
  color: #e8eaed;
`;

const BusinessSectionTitle = styled(SectionTitle)`
  font-size: clamp(3.25rem, 2.2vw, 1.6rem);
  color: #e0a15e;
`;

const Muted = styled.p`
  margin: 0 0 .75rem;
  color: #9aa4b2;
`;

const Input = styled.input<{ $editing?: boolean }>`
  width: 100%;
  padding: .62rem .72rem;
  border-radius: 10px;
  border: 1px solid ${({ $editing }) => ($editing ? 'rgba(255,255,255,.14)' : 'transparent')};
  background: ${({ $editing }) => ($editing ? 'rgba(255,255,255,.04)' : 'transparent')};
  color: #e8eaed;
  box-shadow: none;
  transition: border-color 120ms ease, background-color 120ms ease, color 120ms ease;
  pointer-events: ${({ $editing }) => ($editing ? 'auto' : 'none')};

  &::placeholder {
    color: #8d96a5;
  }

  &:focus {
    outline: none;
    border-color: rgba(255,255,255,.22);
  }

  &:disabled {
    opacity: 1;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: .58rem .66rem;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.14);
  background: rgba(255,255,255,.04);
  color: #e8eaed;
`;

const BusinessProfileRow = styled.div`
  display: grid;
  grid-template-columns: 168px minmax(0, 1fr);
  gap: .85rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    align-items: start;
  }
`;

const BusinessLogoPane = styled.div`
  display: grid;
  gap: .55rem;
  justify-items: start;
  align-self: center;

  @media (max-width: 900px) {
    align-self: start;
  }

  @media (max-width: 760px) {
    justify-items: center;
    width: 100%;
  }
`;

const BusinessLogoMobileRow = styled.div`
  display: contents;

  @media (max-width: 760px) {
    display: grid;
    justify-items: center;
    gap: .45rem;
    width: 100%;
  }
`;

const BusinessLogoFrame = styled.div`
  position: relative;
  width: 132px;
  height: 132px;

  @media (max-width: 760px) {
    width: 144px;
    height: 144px;
  }
`;

const BusinessLogo = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 14px;
  border: 1px dashed rgba(255,255,255,.28);
  display: grid;
  place-items: center;
  overflow: hidden;
  color: #cbd2e1;
  font-weight: 900;
  background: rgba(255,255,255,.03);
  font-size: 1.55rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const LogoEditButton = styled.button`
  position: absolute;
  right: .45rem;
  bottom: .45rem;
  padding: .28rem .52rem;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,.16);
  background: rgba(15, 20, 34, .92);
  color: #cbd2e1;
  font-weight: 800;
  font-size: .72rem;
  cursor: pointer;

  &:disabled {
    opacity: .55;
    cursor: not-allowed;
  }
`;

const BusinessFormPane = styled.div`
  display: grid;
  gap: .65rem;
`;

const BusinessFormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: .65rem;
  padding-left: calc(168px + .85rem);

  @media (max-width: 900px) {
    padding-left: 0;
  }
`;

const ServiceContractsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: start;
`;

const ServiceContractsLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 400px);
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceContractsLead = styled.div`
  display: grid;
  align-content: center;
  gap: .85rem;
  min-height: 100%;
  padding: .25rem 0;
`;

const ServiceContractsLeadTitle = styled.h4`
  margin: 0;
  color: #f3f5f7;
  font-size: clamp(1.35rem, 2.2vw, 1.8rem);
  font-weight: 900;
  letter-spacing: -.03em;
`;

const ServiceContractsLeadCopy = styled.p`
  margin: 0;
  max-width: 36rem;
  color: #9aa4b2;
  line-height: 1.65;
`;

const ServiceContractsLeadActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: .65rem;
  align-items: center;
`;

const ServicePrimaryButton = styled(Button)`
  border: 0;
  ${gradientPillCss};
`;

const ServiceGhostButton = styled(Button)`
  background: transparent;
  border: 1px dashed rgba(255,255,255,.28);
  color: #d7deea;
  box-shadow: none;

  &:hover {
    background: transparent;
    border-color: rgba(255,255,255,.46);
    color: #eef2f7;
  }
`;

const ServiceContractsSidebar = styled.div`
  display: grid;
  gap: 1rem;
  align-content: start;
`;

const ServiceSidebarPanel = styled(Card)`
  display: grid;
  gap: .75rem;
  min-height: 0;
  justify-content: center;
`;

const ServiceTransferSidebarPanel = styled(Card)`
  padding: 0;
  display: grid;
  grid-template-rows: auto auto;
  gap: 0;
  overflow: hidden;
`;

const ServiceTransferSidebarTop = styled.div`
  position: relative;
  padding: 20px 22px;
  display: grid;
  text-align: center;
  gap: .5rem;
  background:
    radial-gradient(circle at 20% 18%, rgba(224, 161, 94, 0.3), transparent 34%),
    radial-gradient(circle at 82% 24%, rgba(73, 215, 255, 0.18), transparent 40%),
    linear-gradient(145deg, #47301d 0%, #24170c 48%, #111827 100%);
`;

const ServiceTransferSidebarLottieWrap = styled.div`
  position: absolute;
  inset: -8px;
  opacity: 0.92;
  pointer-events: none;
  overflow: hidden;

  & > div {
    width: 100%;
    height: 100%;
  }

  & svg {
    width: 100% !important;
    height: 100% !important;
    display: block;
  }
`;

const ServiceTransferSidebarTopOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(10, 7, 25, 0.08) 0%, rgba(10, 7, 25, 0.34) 100%),
    radial-gradient(circle at center, rgba(255, 255, 255, 0.035), transparent 58%);
  pointer-events: none;
`;

const ServiceTransferSidebarText = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: .5rem;
`;

const ServiceTransferSidebarTitle = styled.strong`
  color: #e0a15e;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .08em;
  line-height: 1.2;
`;

const ServiceTransferSidebarCopy = styled.p`
  margin: 0;
  color: rgba(232, 234, 237, 0.92);
  line-height: 1.6;
`;

const ServiceTransferSidebarBottom = styled.div`
  padding: 18px 20px 20px;
  display: grid;
  gap: .75rem;
  align-content: start;
`;

const ServiceSidebarPanelTitle = styled.strong`
  color: #e8eaed;
  font-weight: 900;
`;

const ServiceSidebarPanelCopy = styled.p`
  margin: 0;
  color: #9aa4b2;
  line-height: 1.5;
`;

const ServiceSidebarPanelControls = styled.div`
  display: grid;
  gap: .55rem;
`;

const WorkspaceContractLocationPanel = styled(Card)`
  margin-top: 1rem;
  display: grid;
  gap: .85rem;
  align-content: start;
`;

const WorkspaceContractLocationHeader = styled.div`
  display: grid;
  gap: .35rem;
`;

const WorkspaceContractLocationTitle = styled.strong`
  color: #e8eaed;
  font-weight: 900;
`;

const WorkspaceContractLocationCopy = styled.p`
  margin: 0;
  color: #9aa4b2;
  line-height: 1.6;
`;

const WorkspaceContractLocationMeta = styled.span`
  color: #cfd5df;
  font-size: .92rem;
  line-height: 1.5;
`;

const WorkspaceContractLocationAttachedRow = styled.div`
  display: flex;
  align-items: center;
  gap: .7rem;
  flex-wrap: wrap;
`;

const WorkspaceContractLocationOrgAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  overflow: hidden;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.08);
  color: #eef2f7;
  font-weight: 900;
  font-size: .78rem;
  letter-spacing: .04em;
  flex: 0 0 auto;
  border: 1px solid rgba(255, 255, 255, 0.08);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const WorkspaceContractLocationOrgText = styled.div`
  display: grid;
  gap: .15rem;
  min-width: 0;
`;

const WorkspaceContractLocationOrgName = styled.strong`
  color: #eef2f7;
  font-weight: 800;
  line-height: 1.3;
`;

const WorkspaceContractLocationControls = styled.div`
  display: grid;
  gap: .75rem;
  max-width: 34rem;
`;

const WorkspaceContractLocationField = styled.div`
  display: grid;
  gap: .35rem;
`;

const WorkspaceContractLocationLabel = styled.span`
  color: #9aa4b2;
  font-size: .74rem;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
`;

const ServiceContractCard = styled.article<{ $gradient: string; $glow: string }>`
  position: relative;
  flex: 0 1 420px;
  width: min(100%, 420px);
  min-height: 700px;
  aspect-ratio: 0.68;
  border-radius: 28px;
  overflow: hidden;
  color: #fff;
  background: ${({ $gradient }) => $gradient};
  box-shadow: ${({ $glow }) => $glow};

  @media (max-width: 720px) {
    width: 100%;
    flex-basis: 100%;
  }
`;

const ServiceContractOverlay = styled.div`
  position: absolute;
  inset: 1px;
  border-radius: 27px;
  background: linear-gradient(180deg, rgba(255,255,255,.22), rgba(255,255,255,0) 56%);
  pointer-events: none;
`;

const ServiceContractInner = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ServiceContractBody = styled.div`
  display: grid;
  gap: .9rem;
  padding: 1rem 1rem .9rem;
  flex: 1;
`;

const ServiceContractHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: .75rem;
`;

const ServiceContractEyebrow = styled.span`
  color: rgba(255,255,255,.88);
  font-size: .72rem;
  text-transform: uppercase;
  letter-spacing: .05em;
  font-weight: 900;
`;

const ServiceContractBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: .26rem .58rem;
  border: 1px solid rgba(255,255,255,.26);
  background: rgba(255,255,255,.14);
  color: rgba(255,255,255,.96);
  font-size: .7rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .04em;
  white-space: nowrap;
`;

const ServiceContractTitle = styled.h4`
  margin: 0;
  font-size: clamp(1.9rem, 3.6vw, 2.7rem);
  line-height: .95;
  letter-spacing: -.04em;
  font-weight: 900;
  max-width: 9ch;
`;

const ServiceContractStack = styled.div`
  display: grid;
  gap: .85rem;
`;

const ServiceContractItem = styled.div`
  display: grid;
  gap: .18rem;
`;

const ServiceContractLabel = styled.span`
  color: rgba(255,255,255,.86);
  font-size: .72rem;
  text-transform: uppercase;
  letter-spacing: .04em;
  font-weight: 900;
`;

const ServiceContractValue = styled.span`
  color: #fff;
  font-size: .98rem;
  line-height: 1.45;
  font-weight: 800;
`;

const ServiceContractList = styled.ul`
  margin: 0;
  padding-left: 1rem;
  display: grid;
  gap: .35rem;

  li {
    color: #fff;
    font-size: .96rem;
    line-height: 1.45;
    font-weight: 800;
  }
`;

const ServiceContractOverflow = styled.span`
  color: rgba(255,255,255,.88);
  font-size: .82rem;
  font-weight: 800;
`;

const ServiceContractHours = styled.span`
  color: rgba(255,255,255,.92);
  font-size: .82rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .04em;
`;

const ServiceContractFooter = styled.div<{ $footer: string }>`
  margin-top: auto;
  padding: 1rem;
  border-top: 1px solid rgba(255,255,255,.12);
  background: ${({ $footer }) => $footer};
  display: grid;
  gap: .28rem;
`;

const ServiceContractFooterTitle = styled.div`
  color: #fff;
  font-size: clamp(2rem, 4vw, 2.8rem);
  line-height: .95;
  letter-spacing: -.03em;
  font-weight: 900;
`;

const ServiceContractFooterCopy = styled.div`
  color: rgba(255,255,255,.92);
  font-size: .76rem;
  line-height: 1.5;
  font-weight: 700;
`;

const FlatEditButton = styled.button`
  width: 30px;
  height: 30px;
  border: 0;
  background: transparent;
  color: #9aa4b2;
  display: grid;
  place-items: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #e8eaed;
  }

  &:disabled {
    opacity: .55;
    cursor: not-allowed;
  }
`;

const BusinessHeaderEditButton = styled(FlatEditButton)`
  @media (max-width: 760px) {
    display: none;
  }
`;

const BusinessLogoMobileEditButton = styled.button`
  display: none;

  @media (max-width: 760px) {
    display: inline-flex;
    align-items: center;
    gap: .38rem;
    border: 0;
    background: transparent;
    color: #e8eaed;
    padding: .1rem 0 0;
    font-size: .78rem;
    font-weight: 800;
    letter-spacing: .04em;
    text-transform: uppercase;
    cursor: pointer;
    justify-content: center;
  }

  &:hover {
    color: #f3d3a2;
  }

  &:disabled {
    opacity: .55;
    cursor: not-allowed;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const BusinessProfileServicesLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(300px, 0.82fr);
  gap: 1rem;
  align-items: start;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const BusinessProfileMainColumn = styled.div`
  display: grid;
  gap: .85rem;
  min-width: 0;
`;

const BusinessProfileSidebarColumn = styled.div`
  display: grid;
  gap: 1rem;
  align-content: start;
  min-width: 0;
`;

const BusinessProfileHeaderRow = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: .75rem;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    justify-content: center;
    align-items: center;
    text-align: center;
  }
`;

const BusinessProfileHeaderText = styled.div`
  display: grid;
  gap: .2rem;

  @media (max-width: 900px) {
    justify-items: center;
    text-align: center;
  }
`;

const BusinessProfileServicesLead = styled.div`
  display: grid;
  gap: .85rem;
  align-content: start;
  padding: .15rem 0 0;
`;


const ErrorText = styled.p`
  margin: .5rem 0 0;
  color: #ff9b9b;
  font-weight: 700;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: .6rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BusinessSetupLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 340px);
  gap: 1rem;
  margin-top: 1rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BusinessSetupLead = styled.div`
  display: grid;
  gap: .85rem;
  align-content: center;
  min-height: 100%;
  padding: .2rem 0;
`;

const BusinessSetupLeadTitle = styled.h4`
  margin: 0;
  color: #f3f5f7;
  font-size: clamp(1.35rem, 2vw, 1.7rem);
  font-weight: 900;
  letter-spacing: -.03em;
`;

const BusinessSetupLeadCopy = styled.p`
  margin: 0;
  max-width: 40rem;
  color: #9aa4b2;
  line-height: 1.65;
`;

const BusinessSetupPoints = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: .55rem;
`;

const BusinessSetupPoint = styled.li`
  color: #cfd5df;
  line-height: 1.6;

  strong {
    color: #eef2f6;
    font-weight: 800;
  }
`;

const BusinessSetupCard = styled(Card)`
  display: grid;
  gap: .8rem;
  padding: 1rem;
  align-content: center;
  justify-items: center;
  text-align: center;
`;

const BusinessPrimaryButton = styled(Button)`
  border: 0;
  ${gradientPillCss};
`;

const Name = styled.strong`
  min-width: 0;
  color: #e8eaed;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ModalBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  background: rgba(6, 10, 18, 0.76);
  backdrop-filter: blur(5px);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 140ms ease;
  z-index: 70;
`;

const Modal = styled.section<{ $open: boolean }>`
  position: fixed;
  inset: auto 0 0 0;
  margin: auto;
  top: 0;
  width: min(560px, calc(100vw - 2rem));
  height: fit-content;
  max-height: 88vh;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: linear-gradient(180deg, rgba(14, 20, 35, 0.98), rgba(10, 15, 28, 0.98));
  box-shadow: 0 30px 64px rgba(0, 0, 0, 0.48);
  color: #e8eaed;
  transform: ${({ $open }) => ($open ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.98)')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: transform 160ms ease, opacity 160ms ease;
  z-index: 71;
  overflow: hidden;
`;

const InvitePreviewModal = styled(Card)<{ $open: boolean }>`
  position: fixed;
  inset: auto 0 0 0;
  margin: auto;
  top: 0;
  width: min(720px, calc(100vw - 2rem));
  max-height: min(88vh, 820px);
  padding: 1rem;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: .95rem;
  transform: ${({ $open }) => ($open ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.98)')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: transform 160ms ease, opacity 160ms ease;
  z-index: 71;
  overflow: hidden;

  @media (max-width: 720px) {
    width: calc(100vw - 1rem);
    padding: .95rem;
  }
`;

const InvitePreviewHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
  padding-bottom: .85rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const InvitePreviewBody = styled.div`
  min-height: 0;
  overflow: auto;
  display: grid;
  align-content: start;
  align-items: start;
  grid-auto-rows: max-content;
  gap: .8rem;
  padding-right: .1rem;
`;

const InvitePreviewActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: .6rem;
  flex-wrap: wrap;
  padding-top: .9rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const ServiceTransferPromptModal = styled(Card)<{ $open: boolean }>`
  position: fixed;
  left: 0;
  right: 0;
  top: 50%;
  bottom: auto;
  margin: 0 auto;
  width: min(720px, calc(100vw - 2rem));
  max-height: min(88vh, 820px);
  padding: 0;
  display: grid;
  grid-template-rows: auto auto;
  gap: 0;
  transform: ${({ $open }) => ($open ? 'translateY(-50%) scale(1)' : 'translateY(calc(-50% + 12px)) scale(0.98)')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: transform 160ms ease, opacity 160ms ease;
  z-index: 71;
  overflow: hidden;

  @media (max-width: 720px) {
    width: calc(100vw - 1rem);
  }
`;

const ServiceTransferPromptHero = styled.div`
  position: relative;
  min-height: 240px;
  padding: 22px 24px;
  display: grid;
  place-items: center;
  overflow: hidden;
  background:
    radial-gradient(circle at 20% 18%, rgba(224, 161, 94, 0.34), transparent 34%),
    radial-gradient(circle at 82% 24%, rgba(73, 215, 255, 0.2), transparent 40%),
    linear-gradient(145deg, #47301d 0%, #24170c 48%, #111827 100%);
`;

const ServiceTransferPromptHeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(10, 7, 25, 0.08) 0%, rgba(10, 7, 25, 0.36) 100%),
    radial-gradient(circle at center, rgba(255, 255, 255, 0.04), transparent 58%);
  pointer-events: none;
`;

const ServiceTransferPromptLottieWrap = styled.div`
  position: absolute;
  inset: -8px;
  opacity: 0.92;
  pointer-events: none;
  overflow: hidden;

  & > div {
    width: 100%;
    height: 100%;
  }

  & svg {
    width: 100% !important;
    height: 100% !important;
    display: block;
  }
`;

const ServiceTransferPromptHeroTitle = styled.h2`
  position: relative;
  z-index: 2;
  margin: 0;
  max-width: 420px;
  color: #e0a15e;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: clamp(1.02rem, 2.2vw, 1.38rem);
  line-height: 1.2;
  font-weight: 900;
  text-shadow: 0 10px 28px rgba(13, 8, 26, 0.34);
`;

const ServiceTransferPromptBottom = styled.div`
  padding: 22px 24px 24px;
  display: grid;
  align-content: start;
  gap: 14px;
`;

const ServiceTransferPromptBody = styled.p`
  margin: 0;
  color: #aeb6c2;
  line-height: 1.6;
`;

const ServiceTransferPromptField = styled.div`
  display: grid;
  gap: 8px;
`;

const ServiceTransferPromptActions = styled.div`
  display: grid;
  gap: 10px;
`;

const ServiceTransferPromptDismiss = styled.button`
  background: transparent;
  border: 0;
  box-shadow: none;
  padding: 0;
  min-height: auto;
  color: #9aa4b2;
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
  padding: .9rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 900;
  color: #e8eaed;
`;

const ModalBody = styled.div`
  padding: 1rem;
  display: grid;
  gap: .8rem;
`;

const ModalText = styled.p`
  margin: 0;
  color: #cbd2e1;
  line-height: 1.4;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: .6rem;
  padding: 0 1rem 1rem;
`;

const ModalCard = styled.div`
  display: grid;
  gap: .45rem;
  padding: .72rem .8rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
`;

const InvitePreviewCard = styled(ModalCard)`
  padding: .75rem .85rem;
  border-radius: 12px;
  border-color: rgba(255,255,255,.08);
  background: rgba(255,255,255,.03);
`;

const InviteIdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: .7rem;
  min-width: 0;
`;

const InviteIdentityMeta = styled.div`
  display: grid;
  gap: .15rem;
  min-width: 0;
`;

const InviteIdentityName = styled.span`
  color: #e8eaed;
  font-weight: 800;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ModalLabel = styled.span`
  font-size: .76rem;
  text-transform: uppercase;
  letter-spacing: .05em;
  font-weight: 800;
`;

const ModalValue = styled.span`
  color: #e8eaed;
  font-weight: 800;
  font-size: .92rem;
`;

const InviteMembersList = styled.div`
  display: grid;
  gap: .55rem;
`;

const InviteMemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
  padding: .7rem .8rem;
  border-radius: 10px;
  background: rgba(255,255,255,.035);
  border: 1px solid rgba(255,255,255,.08);
`;

const InviteMemberMeta = styled.div`
  display: grid;
  gap: .2rem;
  min-width: 0;
`;

const InviteMemberName = styled.span`
  color: #eef2f7;
  font-weight: 800;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const InviteMemberEmail = styled.span`
  color: #9aa4b2;
  font-size: .86rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const InviteRolePill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: .26rem .58rem;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.14);
  background: rgba(255,255,255,.06);
  color: #dce4ef;
  font-size: .72rem;
  font-weight: 900;
  letter-spacing: .03em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const QuietBtn = styled(Button)`
  background: rgba(255,255,255,.04);
  color: #d3dae8;
  border: 1px solid rgba(255,255,255,.18);
`;

const InviteDecisionButton = styled(Button)`
  border: 0;
  ${gradientPillCss};
`;

const DangerCard = styled(Card)`
  display: grid;
  gap: .6rem;
  justify-items: center;
  text-align: center;

  & > p {
    margin-inline: auto;
    max-width: 46rem;
  }
`;

const DangerTitle = styled.strong`
  color: #f2d0cc;
  font-weight: 900;
`;

const DangerActions = styled.div`
  display: flex;
  justify-content: center;
  gap: .65rem;
  flex-wrap: wrap;
`;

const DangerButton = styled(Button)`
  background: #a64c44;
  border: 1px solid #a64c44;
  color: #fff;

  &:hover {
    background: #b6574f;
    border-color: #b6574f;
  }
`;

const DangerGhostButton = styled(Button)`
  background: rgba(255,255,255,.04);
  border: 1px solid rgba(248, 113, 113, .34);
  color: #fecaca;
`;

const BusinessPageSkeletonPanel = styled(Card)`
  display: grid;
  gap: .9rem;
`;

const BusinessPageSkeletonRows = styled.div`
  display: grid;
  gap: .55rem;
`;

const BusinessPageSkeletonMemberRow = styled.div`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: .75rem;
  align-items: center;
  padding: .75rem .85rem;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.03);
`;

function formatPaymentMethod(pm: PaymentMethod | null): string {
  if (!pm) return 'No payment method selected';
  const brand = String(pm.brand || 'Card').toUpperCase();
  const last4 = pm.last4 ? `•••• ${pm.last4}` : '••••';
  const month = pm.exp_month ? String(pm.exp_month).padStart(2, '0') : '--';
  const year = pm.exp_year ? String(pm.exp_year).slice(-2) : '--';
  return `${brand} ${last4} · Expires ${month}/${year}`;
}

function isActiveServiceStatus(status: string | null | undefined): boolean {
  const normalized = String(status || '').trim().toLowerCase();
  return normalized === 'active' || normalized === 'trialing';
}

function initialsFromLabel(value: string | null | undefined): string {
  const text = String(value ?? '').trim();
  if (!text) return 'B';
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase() || 'B';
}

type ServiceContractCategory = 'marketing' | 'platform' | 'assets_design' | 'custom';
type ServiceContractTheme = { gradient: string; footer: string; glow: string };

function normalizeServiceContractCategory(
  value: string | null | undefined,
): ServiceContractCategory {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (
    normalized.includes('marketing')
    || normalized === 'mm-basic'
    || normalized === 'mm-silver'
    || normalized === 'mm-gold'
    || normalized === 'mm-platinum'
    || normalized === 'mm_basic'
    || normalized === 'mm_silver'
    || normalized === 'mm_gold'
    || normalized === 'mm_platinum'
  ) {
    return 'marketing';
  }
  if (
    normalized.includes('platform')
    || normalized.includes('digital')
    || normalized.includes('development')
    || normalized === 'pl-basic'
    || normalized === 'pl-silver'
    || normalized === 'pl-gold'
    || normalized === 'pl-platinum'
    || normalized === 'pl_basic'
    || normalized === 'pl_silver'
    || normalized === 'pl_gold'
    || normalized === 'pl_platinum'
  ) {
    return 'platform';
  }
  if (
    normalized.includes('assets')
    || normalized.includes('design')
    || normalized.includes('cybard club')
  ) {
    return 'assets_design';
  }
  return 'custom';
}

function normalizeServiceContractCode(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
}

function getServiceContractTheme(category: ServiceContractCategory): ServiceContractTheme {
  if (category === 'platform') {
    return {
      gradient: 'linear-gradient(135deg, #7d69ff 0%, #4894ff 52%, #16d4c1 100%)',
      footer: 'linear-gradient(180deg, rgba(0,0,0,.02), rgba(0,0,0,.2))',
      glow: '0 26px 72px rgba(53, 123, 255, 0.28)',
    };
  }
  if (category === 'assets_design') {
    return {
      gradient: 'linear-gradient(135deg, #2ed39c 0%, #17a97d 48%, #1493cf 100%)',
      footer: 'linear-gradient(180deg, rgba(0,0,0,.02), rgba(0,0,0,.18))',
      glow: '0 26px 72px rgba(30, 170, 131, 0.28)',
    };
  }
  return {
    gradient: 'linear-gradient(135deg, #ff7a3d 0%, #ff446d 52%, #ff1789 100%)',
    footer: 'linear-gradient(180deg, rgba(0,0,0,.02), rgba(0,0,0,.22))',
    glow: '0 26px 72px rgba(255, 68, 109, 0.28)',
  };
}

function getCurrentContractOfferingNames(service: ServiceWorkspaceCurrentService): string[] {
  const selected = Array.isArray(service.selectedOfferings)
    ? service.selectedOfferings
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
    : [];
  if (selected.length) return Array.from(new Set(selected));
  const snapshotOfferings = Array.isArray(service.displaySnapshot?.offerings)
    ? service.displaySnapshot.offerings
    : [];
  return Array.from(
    new Set(
      snapshotOfferings
        .map((offering) => String(offering?.name ?? '').trim())
        .filter(Boolean),
    ),
  );
}

function formatServiceContractHours(
  minMinutes: number | null | undefined,
  maxMinutes: number | null | undefined,
): string | null {
  const minHours = Number.isFinite(Number(minMinutes)) && Number(minMinutes) > 0
    ? Number(minMinutes) / 60
    : null;
  const maxHours = Number.isFinite(Number(maxMinutes)) && Number(maxMinutes) > 0
    ? Number(maxMinutes) / 60
    : null;
  const normalize = (hours: number) => {
    const rounded = Math.round(hours * 10) / 10;
    return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
  };
  if (minHours !== null && maxHours !== null) {
    return minHours === maxHours
      ? `${normalize(minHours)} service hours/mo`
      : `${normalize(minHours)}-${normalize(maxHours)} service hours/mo`;
  }
  if (minHours !== null) return `${normalize(minHours)}+ service hours/mo`;
  if (maxHours !== null) return `Up to ${normalize(maxHours)} service hours/mo`;
  return null;
}

function readTierHoursSummary(tier: ServiceCatalogTier | null | undefined): string | null {
  if (!tier) return null;
  const metadata =
    tier.metadata && typeof tier.metadata === 'object' && !Array.isArray(tier.metadata)
      ? tier.metadata
      : {};
  const contractedMin = Number(metadata.contractedMonthlyMinutesMin);
  const contractedMax = Number(metadata.contractedMonthlyMinutesMax);
  if (
    (Number.isFinite(contractedMin) && contractedMin > 0)
    || (Number.isFinite(contractedMax) && contractedMax > 0)
  ) {
    return formatServiceContractHours(
      Number.isFinite(contractedMin) ? contractedMin : null,
      Number.isFinite(contractedMax) ? contractedMax : null,
    );
  }

  const deliveryHours = metadata.deliveryHours;
  if (deliveryHours && typeof deliveryHours === 'object' && !Array.isArray(deliveryHours)) {
    const min = Number((deliveryHours as Record<string, unknown>).min);
    const max = Number((deliveryHours as Record<string, unknown>).max);
    const normalize = (hours: number) => {
      const rounded = Math.round(hours * 10) / 10;
      return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
    };
    if (Number.isFinite(min) && min > 0 && Number.isFinite(max) && max > 0) {
      return min === max
        ? `${normalize(min)} service hours/mo`
        : `${normalize(min)}-${normalize(max)} service hours/mo`;
    }
    if (Number.isFinite(min) && min > 0) return `${normalize(min)}+ service hours/mo`;
    if (Number.isFinite(max) && max > 0) return `Up to ${normalize(max)} service hours/mo`;
  }

  return null;
}

function readTierMeetingsSummary(tier: ServiceCatalogTier | null | undefined): string | null {
  if (!tier) return null;
  const metadata =
    tier.metadata && typeof tier.metadata === 'object' && !Array.isArray(tier.metadata)
      ? tier.metadata
      : {};
  const rawCadenceType =
    typeof metadata.clientSuccessCadenceType === 'string'
      ? metadata.clientSuccessCadenceType.trim()
      : '';
  const meetingsRaw = metadata.clientSuccessMeetingsPerMonth;
  const meetingsPerMonth =
    typeof meetingsRaw === 'number'
      ? meetingsRaw
      : typeof meetingsRaw === 'string' && meetingsRaw.trim()
        ? Number.parseInt(meetingsRaw, 10)
        : null;
  const cadenceType = rawCadenceType.toLowerCase();

  if (typeof meetingsPerMonth === 'number' && Number.isFinite(meetingsPerMonth)) {
    if (meetingsPerMonth <= 0) return 'No account alignment meetings';
    if (meetingsPerMonth === 1) return '1 account alignment meeting per month';
    return `${meetingsPerMonth} account alignment meetings per month`;
  }

  if (cadenceType === 'weekly') return '4 account alignment meetings per month';
  if (cadenceType === 'biweekly') return '2 account alignment meetings per month';
  if (cadenceType === 'monthly') return '1 account alignment meeting per month';
  if (cadenceType === 'quarterly') return '1 account alignment meeting per quarter';

  const fallbackCadence = metadata.cadence;
  if (typeof fallbackCadence === 'string' && fallbackCadence.trim()) {
    return fallbackCadence;
  }
  return null;
}

function resolveCurrentContractTier(
  service: ServiceWorkspaceCurrentService,
  catalogServices: ServiceCatalogService[],
): ServiceCatalogTier | null {
  const serviceCode = normalizeServiceContractCode(service.serviceCode);
  const serviceName = normalizeServiceContractCode(service.serviceName);
  const category = normalizeServiceContractCategory(service.serviceCode || service.serviceName);
  const tierSkuId = normalizeServiceContractCode(service.tierSkuId);

  const matchedService =
    catalogServices.find((entry) => normalizeServiceContractCode(entry.code) === serviceCode)
    ?? catalogServices.find((entry) => normalizeServiceContractCategory(entry.category || entry.code || entry.title) === category)
    ?? catalogServices.find((entry) => normalizeServiceContractCode(entry.title) === serviceName)
    ?? null;

  if (!matchedService) return null;
  return matchedService.tiers.find((tier) => normalizeServiceContractCode(tier.skuId) === tierSkuId) ?? null;
}

function formatServiceFamilyLabel(service: ServiceWorkspaceCurrentService): string {
  const name = String(service.serviceName ?? service.serviceCode ?? 'Service').trim() || 'Service';
  return `${name.toUpperCase()} contract`;
}

const PRIMARY_FALLBACK_PERMISSIONS: ClientOrgMember['permissions'] = {
  inviteMembers: true,
  createTickets: true,
  manageBilling: true,
  manageSubscription: true,
};

function formatOrganizationTypeLabel(
  organizationType: 'individual' | 'business' | null | undefined,
) {
  return organizationType === 'business' ? 'Business' : 'Individual';
}

function formatInviteMemberName(member: InvitePreviewMember): string {
  const firstName = member.firstName?.trim() ?? '';
  const lastName = member.lastName?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || member.displayName || member.email || member.uid;
}

function InviteBannerConfetti() {
  const [activePreset, setActivePreset] = useState(() => pickInviteBannerConfettiPreset());

  const handleLoopComplete = useCallback(() => {
    setActivePreset((current) => pickInviteBannerConfettiPreset(current.index));
  }, []);

  return (
    <InviteBannerConfettiLayer
      aria-hidden="true"
      $top={activePreset.preset.top}
      $left={activePreset.preset.left}
      $size={activePreset.preset.size}
      $rotate={activePreset.preset.rotate}
      $scale={activePreset.preset.scale}
      $opacity={activePreset.preset.opacity}
    >
      <Lottie
        animationData={confettiAnimation}
        loop
        autoplay
        onLoopComplete={handleLoopComplete}
      />
    </InviteBannerConfettiLayer>
  );
}

export default function ClientTeamsPage() {
  const nav = useNavigate();
  const {
    user,
    orgId: authOrgId,
    organizations,
    setActiveOrg,
    refreshRole,
  } = useAuth();
  const [orgId, setOrgId] = useState<string | null>(authOrgId ?? null);
  const [orgSwitching, setOrgSwitching] = useState(false);
  const [members, setMembers] = useState<ClientOrgMember[]>([]);
  const [currentServices, setCurrentServices] = useState<ServiceWorkspaceCurrentService[]>([]);
  const [transferableContracts, setTransferableContracts] = useState<ServiceWorkspaceCurrentService[]>([]);
  const [pendingServiceProposals, setPendingServiceProposals] = useState<ServiceWorkspaceProposal[]>([]);
  const [serviceCatalog, setServiceCatalog] = useState<ServiceCatalogService[]>([]);
  const [serviceContractsUnavailable, setServiceContractsUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadedOrgId, setLoadedOrgId] = useState<string | null>(authOrgId ?? null);
  const [error, setError] = useState<string | null>(null);
  const [pendingOrganizationInvites, setPendingOrganizationInvites] = useState<OrgInviteNotificationMeta[]>([]);
  const [selectedInviteMeta, setSelectedInviteMeta] = useState<OrgInviteNotificationMeta | null>(null);
  const [invitePreviewOpen, setInvitePreviewOpen] = useState(false);
  const [invitePreviewLoading, setInvitePreviewLoading] = useState(false);
  const [invitePreviewError, setInvitePreviewError] = useState<string | null>(null);
  const [activeInvitePreview, setActiveInvitePreview] = useState<InvitePreview | null>(null);
  const [inviteDecisionPending, setInviteDecisionPending] = useState<'accept' | 'reject' | null>(null);
  const [inviteDecisionError, setInviteDecisionError] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentMethodCheckStatus, setPaymentMethodCheckStatus] = useState<'ok' | 'unavailable'>('ok');
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionCheckStatus, setSubscriptionCheckStatus] = useState<'ok' | 'unavailable'>('ok');
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState<{ uid: string; name: string } | null>(null);
  const [transferPaymentMethodId, setTransferPaymentMethodId] = useState<string>('');
  const [transferModalError, setTransferModalError] = useState<string | null>(null);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [deleteOrgModalOpen, setDeleteOrgModalOpen] = useState(false);
  const [deleteOrgSubmitting, setDeleteOrgSubmitting] = useState(false);
  const [leaveOrgModalOpen, setLeaveOrgModalOpen] = useState(false);
  const [leaveOrgSubmitting, setLeaveOrgSubmitting] = useState(false);
  const [leaveOrgError, setLeaveOrgError] = useState<string | null>(null);

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [businessProfile, setBusinessProfile] = useState<ClientOrgBusinessProfile | null>(null);
  const [businessForm, setBusinessForm] = useState({
    businessName: '',
    logoURL: '',
    logoStoragePath: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    address: '',
  });
  const [businessSaving, setBusinessSaving] = useState(false);
  const [businessNotice, setBusinessNotice] = useState<string | null>(null);
  const [businessEditMode, setBusinessEditMode] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [selectedTransferContractId, setSelectedTransferContractId] = useState<string>('');
  const [selectedTransferTargetOrgId, setSelectedTransferTargetOrgId] = useState<string>('');
  const [transferContractSubmitting, setTransferContractSubmitting] = useState(false);
  const [pendingAcceptedBusinessInvite, setPendingAcceptedBusinessInvite] = useState<{
    orgId: string;
    businessName: string;
  } | null>(null);
  const [serviceTransferPrompt, setServiceTransferPrompt] = useState<{
    orgId: string;
    businessName: string;
  } | null>(null);
  const [serviceTransferPromptError, setServiceTransferPromptError] = useState<string | null>(null);
  const [dismissedServiceTransferPromptOrgId, setDismissedServiceTransferPromptOrgId] = useState<string | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement | null>(null);
  const loadDataRequestIdRef = useRef(0);
  const openInviteModal = useCallback(() => setInviteModalOpen(true), []);

  useEffect(() => {
    setOrgId(authOrgId ?? null);
  }, [authOrgId]);

  const loadPendingOrganizationInvites = useCallback(async () => {
    if (!user) {
      setPendingOrganizationInvites([]);
      return;
    }

    try {
      const response = await apiFetch<any>('/api/notifications?limit=100');
      const rows: any[] = Array.isArray(response)
        ? response
        : (response?.items ?? response?.notifications ?? []);

      const next = rows
        .map((row: any) => getOrgInviteNotificationMeta(row))
        .filter((invite): invite is OrgInviteNotificationMeta => Boolean(invite))
        .filter(
          (invite) =>
            invite.inviteStatus === null
            || invite.inviteStatus === 'pending',
        );

      const uniqueByToken = new Map<string, OrgInviteNotificationMeta>();
      next.forEach((invite) => {
        uniqueByToken.set(`${invite.orgId}:${invite.token}`, invite);
      });
      const uniqueInvites = Array.from(uniqueByToken.values());
      if (!uniqueInvites.length) {
        setPendingOrganizationInvites([]);
        return;
      }

      const validatedInvites = await Promise.allSettled(
        uniqueInvites.map(async (invite) => {
          const preview = await apiFetch<InvitePreview>(
            `/api/invites/preview?org=${encodeURIComponent(invite.orgId)}&token=${encodeURIComponent(invite.token)}`,
          );
          return {
            ...invite,
            businessName: preview.businessName || invite.businessName,
            invitedRole: preview.invitedRole,
          };
        }),
      );

      setPendingOrganizationInvites(
        validatedInvites
          .filter((result): result is PromiseFulfilledResult<OrgInviteNotificationMeta> => result.status === 'fulfilled')
          .map((result) => result.value),
      );
    } catch {
      setPendingOrganizationInvites([]);
    }
  }, [user]);

  useEffect(() => {
    void loadPendingOrganizationInvites();
  }, [loadPendingOrganizationInvites]);

  useEffect(() => {
    const onNotificationsUpdated = () => {
      void loadPendingOrganizationInvites();
    };
    window.addEventListener('notifications:updated', onNotificationsUpdated);
    return () => {
      window.removeEventListener('notifications:updated', onNotificationsUpdated);
    };
  }, [loadPendingOrganizationInvites]);

  const openInvitePreview = useCallback(async (invite: OrgInviteNotificationMeta) => {
    setSelectedInviteMeta(invite);
    setInvitePreviewOpen(true);
    setInvitePreviewLoading(true);
    setInvitePreviewError(null);
    setInviteDecisionPending(null);
    setInviteDecisionError(null);
    try {
      const preview = await apiFetch<InvitePreview>(
        `/api/invites/preview?org=${encodeURIComponent(invite.orgId)}&token=${encodeURIComponent(invite.token)}`,
      );
      setActiveInvitePreview(preview);
    } catch (e: any) {
      setActiveInvitePreview(null);
      setInvitePreviewError(e?.message || 'Failed to load invite details.');
    } finally {
      setInvitePreviewLoading(false);
    }
  }, []);

  const closeInvitePreview = useCallback(() => {
    setInvitePreviewOpen(false);
    setInvitePreviewLoading(false);
    setInvitePreviewError(null);
    setActiveInvitePreview(null);
    setSelectedInviteMeta(null);
    setInviteDecisionPending(null);
    setInviteDecisionError(null);
  }, []);

  const handleInviteDecision = useCallback(
    async (action: 'accept' | 'reject') => {
      if (!selectedInviteMeta || inviteDecisionPending) return;

      setInviteDecisionPending(action);
      setInviteDecisionError(null);
      try {
        const acceptedInviteRole =
          activeInvitePreview?.invitedRole ?? selectedInviteMeta.invitedRole;
        const acceptedBusinessName =
          activeInvitePreview?.businessName?.trim()
          || selectedInviteMeta.businessName?.trim()
          || 'this business organization';
        const response = await apiFetch<{ orgId?: string }>(`/api/invites/${action}`, {
          method: 'POST',
          body: JSON.stringify({
            orgId: selectedInviteMeta.orgId,
            token: selectedInviteMeta.token,
          }),
        });

        setPendingOrganizationInvites((prev) =>
          prev.filter(
            (invite) =>
              !(
                invite.orgId === selectedInviteMeta.orgId
                && invite.token === selectedInviteMeta.token
              ),
          ),
        );
        try {
          window.dispatchEvent(new CustomEvent('notifications:updated'));
        } catch {}

        if (action === 'accept') {
          const nextOrgId =
            typeof response?.orgId === 'string' && response.orgId.trim()
              ? response.orgId.trim()
              : selectedInviteMeta.orgId;
          if (acceptedInviteRole === 'owner') {
            setPendingAcceptedBusinessInvite({
              orgId: nextOrgId,
              businessName: acceptedBusinessName,
            });
          }
          setOrgId(nextOrgId);
          await refreshRole();
          try {
            await setActiveOrg(nextOrgId);
          } catch {}
        }

        closeInvitePreview();
        void loadPendingOrganizationInvites();
      } catch (e: any) {
        setInviteDecisionError(e?.message || `Failed to ${action} invite.`);
      } finally {
        setInviteDecisionPending(null);
      }
    },
    [
      activeInvitePreview,
      closeInvitePreview,
      inviteDecisionPending,
      loadPendingOrganizationInvites,
      refreshRole,
      selectedInviteMeta,
      setActiveOrg,
    ],
  );

  const effectiveMembers = useMemo(() => {
    if (members.length === 0 && user) {
      const display = typeof user.displayName === 'string' ? user.displayName.trim() : '';
      const parts = display ? display.split(/\s+/).filter(Boolean) : [];
      const firstName = parts[0] ?? null;
      const lastName = parts.length > 1 ? parts.slice(1).join(' ') : null;
      const fallbackMember: ClientOrgMember = {
        uid: user.uid,
        orgId: orgId ?? authOrgId ?? user.uid,
        email: user.email ?? null,
        displayName: display || null,
        firstName,
        lastName,
        photoURL: user.photoURL ?? null,
        role: 'owner',
        permissions: { ...PRIMARY_FALLBACK_PERMISSIONS },
        isPrimaryAccountHolder: true,
      };
      return [fallbackMember];
    }

    if (members.length === 1) {
      const only = members[0];
      if (!only.isPrimaryAccountHolder && only.role !== 'owner') {
        return [{ ...only, isPrimaryAccountHolder: true }];
      }
    }

    return members;
  }, [authOrgId, members, orgId, user]);

  const primaryAccountHolder = useMemo(
    () =>
      effectiveMembers.find((member) => member.isPrimaryAccountHolder)
      || effectiveMembers.find((member) => member.role === 'owner')
      || null,
    [effectiveMembers],
  );

  const myMembership = useMemo(
    () => effectiveMembers.find((member) => member.uid === user?.uid) ?? null,
    [effectiveMembers, user?.uid],
  );
  const canTransferPrimary = Boolean(
    myMembership?.isPrimaryAccountHolder || myMembership?.role === 'owner',
  );
  const hasPaymentMethodOnFile = paymentMethods.length > 0;
  const hasActiveService = isActiveServiceStatus(subscriptionStatus);
  const requiresPaymentMethodForTransfer = hasActiveService;
  const isSubscriptionVerificationUnavailable = subscriptionCheckStatus !== 'ok';
  const isPaymentMethodVerificationUnavailable = paymentMethodCheckStatus !== 'ok';
  const isTransferVerificationUnavailable =
    isSubscriptionVerificationUnavailable
    || (requiresPaymentMethodForTransfer && isPaymentMethodVerificationUnavailable);
  const selectedTransferPaymentMethod = useMemo(
    () => paymentMethods.find((method) => method.id === transferPaymentMethodId) ?? null,
    [paymentMethods, transferPaymentMethodId],
  );
  const canConfirmTransfer = Boolean(
    transferTarget
    && !transferSubmitting
    && !isTransferVerificationUnavailable
    && (!requiresPaymentMethodForTransfer || hasPaymentMethodOnFile),
  );
  const activeOrganization = useMemo(
    () =>
      organizations.find((organization) => organization.orgId === (orgId ?? authOrgId ?? ''))
      ?? organizations.find((organization) => organization.orgId === (authOrgId ?? ''))
      ?? null,
    [authOrgId, orgId, organizations],
  );
  const activeWorkspaceOrgId = (orgId ?? authOrgId ?? '').trim();
  const hasAttachedServiceContract = currentServices.length > 0;
  const businessTransferableContracts = useMemo(
    () =>
      transferableContracts.filter((contract) => {
        const sourceType = contract.attachedOrganization?.organizationType?.trim().toLowerCase() ?? null;
        return !sourceType || sourceType === 'individual';
      }),
    [transferableContracts],
  );
  const individualReturnableContracts = useMemo(
    () =>
      transferableContracts.filter(
        (contract) =>
          contract.attachedOrganization?.organizationType?.trim().toLowerCase() === 'business',
      ),
    [transferableContracts],
  );
  const manageableContracts = useMemo(
    () =>
      Array.from(
        new Map(
          [...currentServices, ...transferableContracts].map((contract) => [contract.id, contract] as const),
        ).values(),
      ),
    [currentServices, transferableContracts],
  );
  const visibleTransferContracts = useMemo(() => {
    if (businessProfile?.organizationType === 'individual') {
      return manageableContracts;
    }
    return businessTransferableContracts;
  }, [businessProfile?.organizationType, businessTransferableContracts, manageableContracts]);
  const selectedTransferContract = useMemo(
    () =>
      visibleTransferContracts.find((contract) => contract.id === selectedTransferContractId)
      ?? visibleTransferContracts[0]
      ?? null,
    [selectedTransferContractId, visibleTransferContracts],
  );
  const selectedTransferContractAttachedOrgId =
    selectedTransferContract?.attachedOrganization?.id?.trim()
    || null;
  const primaryManagedOrganizations = useMemo(
    () => organizations.filter((organization) => organization.isPrimaryAccountHolder),
    [organizations],
  );
  const availableTransferTargetOrganizations = useMemo(
    () =>
      primaryManagedOrganizations.filter(
        (organization) => organization.orgId !== selectedTransferContractAttachedOrgId,
      ),
    [primaryManagedOrganizations, selectedTransferContractAttachedOrgId],
  );
  const selectedTransferTargetOrganization = useMemo(
    () =>
      availableTransferTargetOrganizations.find((organization) => organization.orgId === selectedTransferTargetOrgId)
      ?? null,
    [availableTransferTargetOrganizations, selectedTransferTargetOrgId],
  );
  const selectedTransferContractLocationLabel = useMemo(() => {
    const attachedOrganization = selectedTransferContract?.attachedOrganization;
    if (!attachedOrganization) return 'Your individual workspace';
    return (
      attachedOrganization.businessName?.trim()
      || attachedOrganization.name?.trim()
      || 'Another workspace'
    );
  }, [selectedTransferContract]);
  const serviceTransferPromptBusinessName =
    serviceTransferPrompt?.businessName.trim()
    || businessProfile?.businessName?.trim()
    || activeOrganization?.businessName?.trim()
    || activeOrganization?.orgName?.trim()
    || 'this business organization';

  const businessFormBaseline = useMemo(
    () => ({
      businessName: businessProfile?.businessName ?? '',
      logoURL: businessProfile?.logoURL ?? '',
      logoStoragePath: businessProfile?.logoStoragePath ?? '',
      contactName: businessProfile?.contactName ?? '',
      contactEmail: businessProfile?.contactEmail ?? '',
      contactPhone: formatUsPhone(businessProfile?.contactPhone ?? ''),
      website: businessProfile?.website ?? '',
      address: businessProfile?.address ?? '',
    }),
    [businessProfile],
  );

  const businessFormDirty = useMemo(
    () =>
      businessForm.businessName !== businessFormBaseline.businessName
      || businessForm.logoURL !== businessFormBaseline.logoURL
      || businessForm.logoStoragePath !== businessFormBaseline.logoStoragePath
      || businessForm.contactName !== businessFormBaseline.contactName
      || businessForm.contactEmail !== businessFormBaseline.contactEmail
      || businessForm.contactPhone !== businessFormBaseline.contactPhone
      || businessForm.website !== businessFormBaseline.website
      || businessForm.address !== businessFormBaseline.address,
    [businessForm, businessFormBaseline],
  );
  const hasBusinessProfile = useMemo(() => {
    if (!businessProfile) return false;
    return Boolean(
      businessProfile.isComplete
      || (businessProfile.businessName ?? '').trim()
      || (businessProfile.logoURL ?? '').trim()
      || (businessProfile.contactName ?? '').trim()
      || (businessProfile.contactEmail ?? '').trim()
      || (businessProfile.contactPhone ?? '').trim()
      || (businessProfile.website ?? '').trim()
      || (businessProfile.address ?? '').trim(),
    );
  }, [businessProfile]);
  const showBusinessSetupPrompt = Boolean(
    businessProfile
    && !hasBusinessProfile
    && !businessEditMode,
  );
  const hasPrimaryBusinessWorkspace = useMemo(
    () =>
      organizations.some(
        (organization) =>
          organization.orgId !== activeWorkspaceOrgId
          && organization.organizationType === 'business'
          && organization.isPrimaryAccountHolder,
      ),
    [activeWorkspaceOrgId, organizations],
  );
  const showCreateBusinessProfileCard = showBusinessSetupPrompt && !hasPrimaryBusinessWorkspace;

  const loadData = useCallback(async (options?: { force?: boolean }) => {
    const requestId = ++loadDataRequestIdRef.current;
    const force = options?.force === true;
    const targetOrgId = orgId ?? authOrgId ?? undefined;

    if (!user) {
      if (requestId !== loadDataRequestIdRef.current) return;
      setLoading(false);
      setOrgId(null);
      setMembers([]);
      setCurrentServices([]);
      setTransferableContracts([]);
      setPendingServiceProposals([]);
      setServiceCatalog([]);
      setServiceContractsUnavailable(false);
      setBusinessProfile(null);
      setLoadedOrgId(null);
      setBusinessForm({
        businessName: '',
        logoURL: '',
        logoStoragePath: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        website: '',
        address: '',
      });
      setError(null);
      setPaymentMethods([]);
      setSubscriptionStatus(null);
      setPaymentMethodCheckStatus('ok');
      setSubscriptionCheckStatus('ok');
      setBusinessEditMode(false);
      return;
    }

    setLoading(true);
    setError(null);
    setBusinessNotice(null);
    try {
      const [[nextMembers, nextBusinessProfile], nextWorkspaceSummary, nextWorkspaceCatalog] = await Promise.all([
        Promise.all([
          fetchClientOrgMembers(targetOrgId, { force }),
          fetchClientOrgBusinessProfile(targetOrgId, { force }),
        ]),
        fetchServiceWorkspaceSummary(targetOrgId, { force }).catch(() => null),
        fetchServiceWorkspaceCatalog({ force }).catch(() => null),
      ]);

      let nextPaymentMethods: PaymentMethod[] = [];
      try {
        nextPaymentMethods = await fetchBillingPaymentMethods({ force });
        setPaymentMethodCheckStatus('ok');
      } catch {
        nextPaymentMethods = [];
        setPaymentMethodCheckStatus('unavailable');
      }

      let nextSubscriptionStatus: string | null = null;
      try {
        const subscriptionResponse = await fetchBillingSubscription({ force });
        nextSubscriptionStatus =
          typeof subscriptionResponse?.status === 'string'
            ? subscriptionResponse.status
            : null;
        setSubscriptionCheckStatus('ok');
      } catch {
        nextSubscriptionStatus = null;
        setSubscriptionCheckStatus('unavailable');
      }

      if (requestId !== loadDataRequestIdRef.current) return;

      const resolvedOrgId =
        nextMembers.find((member) => member.orgId)?.orgId
        || targetOrgId
        || null;

      if (!orgId && resolvedOrgId) {
        setOrgId(resolvedOrgId);
      }
      setLoadedOrgId(resolvedOrgId ?? targetOrgId ?? null);
      setMembers(nextMembers);
      setCurrentServices(
        Array.isArray(nextWorkspaceSummary?.currentServices)
          ? nextWorkspaceSummary.currentServices.filter((service) => isActiveServiceStatus(service.status))
          : [],
      );
      setTransferableContracts(
        Array.isArray(nextWorkspaceSummary?.transferableContracts)
          ? nextWorkspaceSummary.transferableContracts.filter((service) => isActiveServiceStatus(service.status))
          : [],
      );
      setPendingServiceProposals(
        Array.isArray(nextWorkspaceSummary?.pendingProposals)
          ? nextWorkspaceSummary.pendingProposals
          : [],
      );
      setServiceCatalog(Array.isArray(nextWorkspaceCatalog?.services) ? nextWorkspaceCatalog.services : []);
      setServiceContractsUnavailable(!nextWorkspaceSummary);
      setBusinessProfile(nextBusinessProfile);
      setBusinessForm({
        businessName: nextBusinessProfile.businessName ?? '',
        logoURL: nextBusinessProfile.logoURL ?? '',
        logoStoragePath: nextBusinessProfile.logoStoragePath ?? '',
        contactName: nextBusinessProfile.contactName ?? '',
        contactEmail: nextBusinessProfile.contactEmail ?? '',
        contactPhone: formatUsPhone(nextBusinessProfile.contactPhone ?? ''),
        website: nextBusinessProfile.website ?? '',
        address: nextBusinessProfile.address ?? '',
      });
      setPaymentMethods(nextPaymentMethods);
      setSubscriptionStatus(nextSubscriptionStatus);
      setTransferPaymentMethodId(
        nextPaymentMethods.find((method) => method.isDefault)?.id
          ?? nextPaymentMethods[0]?.id
          ?? '',
      );
      setBusinessEditMode(false);
    } catch (e: any) {
      if (requestId !== loadDataRequestIdRef.current) return;
      setError(e?.message || 'Failed to load organization team data');
      setOrgId(orgId ?? authOrgId ?? null);
      setMembers([]);
      setCurrentServices([]);
      setTransferableContracts([]);
      setPendingServiceProposals([]);
      setServiceCatalog([]);
      setServiceContractsUnavailable(false);
      setBusinessProfile(null);
      setLoadedOrgId(targetOrgId ?? null);
      setBusinessForm({
        businessName: '',
        logoURL: '',
        logoStoragePath: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        website: '',
        address: '',
      });
      setPaymentMethods([]);
      setSubscriptionStatus(null);
      setPaymentMethodCheckStatus('unavailable');
      setSubscriptionCheckStatus('unavailable');
      setBusinessEditMode(false);
    } finally {
      if (requestId === loadDataRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [authOrgId, orgId, user]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!visibleTransferContracts.length) {
      setSelectedTransferContractId('');
      return;
    }
    setSelectedTransferContractId((current) =>
      visibleTransferContracts.some((contract) => contract.id === current)
        ? current
        : visibleTransferContracts[0]?.id ?? '',
    );
  }, [visibleTransferContracts]);

  useEffect(() => {
    const canShowIndividualContractManager =
      businessProfile?.organizationType === 'individual'
      && businessProfile?.canEdit
      && manageableContracts.length > 0;

    if (!canShowIndividualContractManager || !availableTransferTargetOrganizations.length) {
      setSelectedTransferTargetOrgId('');
      return;
    }

    setSelectedTransferTargetOrgId((current) => {
      const currentIsAvailable = availableTransferTargetOrganizations.some(
        (organization) => organization.orgId === current,
      );

      if (
        current
        && currentIsAvailable
      ) {
        return current;
      }

      const preferredActiveOrg = availableTransferTargetOrganizations.find(
        (organization) =>
          organization.orgId === activeWorkspaceOrgId,
      );
      if (preferredActiveOrg) return preferredActiveOrg.orgId;

      return availableTransferTargetOrganizations[0]?.orgId ?? '';
    });
  }, [
    activeWorkspaceOrgId,
    availableTransferTargetOrganizations,
    businessProfile?.canEdit,
    businessProfile?.organizationType,
    manageableContracts.length,
  ]);

  useEffect(() => {
    if (loading) return;
    if (!businessProfile) return;

    const activeBusinessOrgId = (orgId ?? authOrgId ?? '').trim();
    if (!activeBusinessOrgId) {
      return;
    }

    const profileOrgId = (businessProfile?.orgId ?? '').trim();
    if (!profileOrgId || profileOrgId !== activeBusinessOrgId) {
      return;
    }

    if (
      businessProfile.organizationType !== 'business'
      || !businessProfile.isPrimaryAccountHolder
      || hasAttachedServiceContract
    ) {
      setPendingAcceptedBusinessInvite(null);
      return;
    }

    if (!businessTransferableContracts.length) {
      setPendingAcceptedBusinessInvite(null);
      return;
    }

    if (dismissedServiceTransferPromptOrgId === activeBusinessOrgId) {
      setPendingAcceptedBusinessInvite(null);
      return;
    }

    const acceptedInviteTargetsActiveOrg = pendingAcceptedBusinessInvite
      ? pendingAcceptedBusinessInvite.orgId === activeBusinessOrgId
      : false;

    if (!acceptedInviteTargetsActiveOrg && serviceTransferPrompt?.orgId === activeBusinessOrgId) {
      return;
    }

    setServiceTransferPrompt({
      orgId: activeBusinessOrgId,
      businessName:
        businessProfile.businessName?.trim()
        || pendingAcceptedBusinessInvite?.businessName
        || activeOrganization?.businessName?.trim()
        || activeOrganization?.orgName?.trim()
        || 'this business organization',
    });
    setServiceTransferPromptError(null);
    setPendingAcceptedBusinessInvite(null);
  }, [
    activeOrganization?.businessName,
    activeOrganization?.orgName,
    authOrgId,
    businessProfile,
    dismissedServiceTransferPromptOrgId,
    hasAttachedServiceContract,
    loading,
    orgId,
    pendingAcceptedBusinessInvite,
    serviceTransferPrompt?.orgId,
    businessTransferableContracts.length,
  ]);

  const closeServiceTransferPrompt = useCallback(() => {
    setDismissedServiceTransferPromptOrgId((current) =>
      serviceTransferPrompt?.orgId ?? current,
    );
    setServiceTransferPrompt(null);
    setServiceTransferPromptError(null);
  }, [serviceTransferPrompt?.orgId]);

  const handleOrganizationSwitch = useCallback(async (nextOrgId: string) => {
    const trimmed = nextOrgId.trim();
    if (!trimmed || trimmed === authOrgId) {
      setOrgId(trimmed || null);
      return;
    }

    setOrgSwitching(true);
    setLoading(true);
    setError(null);
    setOrgId(trimmed);
    try {
      await setActiveOrg(trimmed);
    } catch (e: any) {
      setError(e?.message || 'Failed to switch organization context');
    } finally {
      setOrgSwitching(false);
    }
  }, [authOrgId, setActiveOrg]);

  const closeInviteModal = useCallback(() => {
    setInviteModalOpen(false);
    void loadData();
  }, [loadData]);

  const handleTransferServiceContract = useCallback(async (options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
    successNotice?: string;
    targetOrgId?: string;
    currentServiceId?: string;
  }) => {
    const currentServiceId = (options?.currentServiceId ?? selectedTransferContractId).trim();
    const targetOrgId = (options?.targetOrgId ?? orgId ?? authOrgId ?? '').trim();
    if (!currentServiceId || !targetOrgId) {
      const message = 'No active service contract is available to transfer right now.';
      setError(message);
      options?.onError?.(message);
      return false;
    }
    try {
      setTransferContractSubmitting(true);
      setError(null);
      setBusinessNotice(null);
      await transferCurrentClientServiceToOrganization({
        orgId: targetOrgId,
        currentServiceId,
      });
      setBusinessNotice(options?.successNotice ?? 'Service contract transferred to this business.');
      await loadData({ force: true });
      options?.onSuccess?.();
      return true;
    } catch (e: any) {
      const message = e?.message || 'Failed to transfer service contract';
      setError(message);
      options?.onError?.(message);
      return false;
    } finally {
      setTransferContractSubmitting(false);
    }
  }, [authOrgId, loadData, orgId, selectedTransferContractId]);

  const handleConfirmServiceTransferPrompt = useCallback(async () => {
    if (!serviceTransferPrompt) return;
    setServiceTransferPromptError(null);
    await handleTransferServiceContract({
      successNotice: `Service contract transferred to ${serviceTransferPrompt.businessName}.`,
      onSuccess: closeServiceTransferPrompt,
      onError: (message) => setServiceTransferPromptError(message),
    });
  }, [closeServiceTransferPrompt, handleTransferServiceContract, serviceTransferPrompt]);

  const saveBusinessProfile = useCallback(async () => {
    if (!businessProfile?.canEdit) return;
    if (businessForm.contactPhone.trim() && !isValidUsPhone(businessForm.contactPhone)) {
      setError('Please enter a valid 10-digit business contact phone number.');
      return;
    }
    try {
      setBusinessSaving(true);
      setBusinessNotice(null);
      setError(null);
      const next = await updateClientOrgBusinessProfile({
        orgId: orgId ?? authOrgId ?? undefined,
        businessName: businessForm.businessName,
        logoURL: businessForm.logoURL,
        logoStoragePath: businessForm.logoStoragePath,
        contactName: businessForm.contactName,
        contactEmail: businessForm.contactEmail,
        contactPhone: businessForm.contactPhone,
        website: businessForm.website,
        address: businessForm.address,
      });
      setBusinessProfile(next);
      setBusinessForm({
        businessName: next.businessName ?? '',
        logoURL: next.logoURL ?? '',
        logoStoragePath: next.logoStoragePath ?? '',
        contactName: next.contactName ?? '',
        contactEmail: next.contactEmail ?? '',
        contactPhone: formatUsPhone(next.contactPhone ?? ''),
        website: next.website ?? '',
        address: next.address ?? '',
      });
      setBusinessNotice('Business profile saved.');
      setBusinessEditMode(false);
      await refreshRole();
    } catch (e: any) {
      setError(e?.message || 'Failed to save business profile');
    } finally {
      setBusinessSaving(false);
    }
  }, [authOrgId, businessForm, businessProfile?.canEdit, orgId, refreshRole]);

  const triggerLogoUpload = useCallback(() => {
    if (!businessProfile?.canEdit) return;
    logoFileInputRef.current?.click();
  }, [businessProfile?.canEdit]);

  const handleLogoFileChange = useCallback(async (file: File | null) => {
    if (!file || !businessProfile?.canEdit || !user) return;
    const mime = file.type.trim().toLowerCase();
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(mime)) {
      setError('Business logo must be PNG, JPG, or WEBP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Business logo must be 5MB or smaller');
      return;
    }

    const extension = mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : 'jpg';
    const safeOrg = (orgId ?? authOrgId ?? user.uid).replace(/[^a-zA-Z0-9_-]/g, '_');
    const objectPath = `org-logos/${safeOrg}/${Date.now()}.${extension}`;

    try {
      setLogoUploading(true);
      setError(null);
      setBusinessNotice(null);
      const objectRef = storageRef(storage, objectPath);
      await uploadBytes(objectRef, file, {
        contentType: mime,
        cacheControl: 'public,max-age=3600',
      });
      const logoURL = await getDownloadURL(objectRef);
      const next = await updateClientOrgBusinessProfile({
        orgId: orgId ?? authOrgId ?? undefined,
        logoURL,
        logoStoragePath: objectPath,
      });
      setBusinessProfile(next);
      setBusinessForm((prev) => ({
        ...prev,
        logoURL: next.logoURL ?? '',
        logoStoragePath: next.logoStoragePath ?? '',
      }));
      setBusinessNotice('Business logo updated.');
      await refreshRole();
    } catch (e: any) {
      setError(e?.message || 'Failed to upload business logo');
    } finally {
      setLogoUploading(false);
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = '';
      }
    }
  }, [authOrgId, businessProfile?.canEdit, orgId, refreshRole, user]);

  const transferPrimaryAccountHolder = async (targetUid: string): Promise<boolean> => {
    if (!canTransferPrimary) {
      setError('Only the current primary account holder can transfer primary access.');
      return false;
    }
    const currentPrimaryUid = primaryAccountHolder?.uid;
    if (!currentPrimaryUid || currentPrimaryUid === targetUid) return false;

    try {
      setError(null);
      await transferClientOrgPrimaryAccountHolder(targetUid, {
        orgId: orgId ?? authOrgId ?? undefined,
      });
      await loadData({ force: true });
      return true;
    } catch (e: any) {
      setError(e?.message || 'Failed to transfer primary account holder');
      return false;
    }
  };

  const handleConfirmPrimaryTransfer = async () => {
    if (!transferTarget) return;
    if (!canTransferPrimary) {
      setTransferModalError('Only the current primary account holder can transfer primary access.');
      return;
    }
    if (isSubscriptionVerificationUnavailable) {
      setTransferModalError('Subscription status could not be verified. Open Billing and retry.');
      return;
    }
    if (requiresPaymentMethodForTransfer) {
      if (isPaymentMethodVerificationUnavailable) {
        setTransferModalError('Payment methods could not be verified. Open Billing and retry.');
        return;
      }
      if (!hasPaymentMethodOnFile) {
        setTransferModalError('An active service requires a payment method before primary transfer.');
        return;
      }

      const selectedId =
        transferPaymentMethodId
        || paymentMethods.find((method) => method.isDefault)?.id
        || paymentMethods[0]?.id;
      if (!selectedId) {
        setTransferModalError('Select a payment method before confirming transfer.');
        return;
      }

      try {
        const currentDefaultId = paymentMethods.find((method) => method.isDefault)?.id || '';
        if (selectedId !== currentDefaultId) {
          await apiFetch(`/api/billing/payment-methods/${selectedId}/default`, { method: 'POST' });
          invalidateBillingCache('paymentMethods');
        }
      } catch (e: any) {
        setTransferModalError(e?.message || 'Failed to set the selected payment method as default.');
        return;
      }
    }

    setTransferSubmitting(true);
    const transferred = await transferPrimaryAccountHolder(transferTarget.uid);
    setTransferSubmitting(false);
    if (!transferred) return;

    setTransferModalOpen(false);
    setTransferTarget(null);
    setTransferModalError(null);
  };

  const handleDeleteOrganization = useCallback(async () => {
    if (!businessProfile?.isPrimaryAccountHolder || deleteOrgSubmitting) return;
    try {
      setDeleteOrgSubmitting(true);
      setError(null);
      setBusinessNotice(null);
      await deleteClientOrganization(orgId ?? authOrgId ?? undefined);
      setDeleteOrgModalOpen(false);
      await refreshRole();
      nav('/dashboard/profile');
    } catch (e: any) {
      setError(e?.message || 'Failed to delete organization');
    } finally {
      setDeleteOrgSubmitting(false);
    }
  }, [
    authOrgId,
    businessProfile?.isPrimaryAccountHolder,
    deleteOrgSubmitting,
    nav,
    orgId,
    refreshRole,
  ]);

  const handleLeaveOrganization = useCallback(async () => {
    if (businessProfile?.isPrimaryAccountHolder || leaveOrgSubmitting) return;
    try {
      setLeaveOrgSubmitting(true);
      setLeaveOrgError(null);
      setError(null);
      setBusinessNotice(null);
      const result = await leaveClientOrganization(orgId ?? authOrgId ?? undefined);
      setLeaveOrgModalOpen(false);
      setOrgId(result.nextActiveOrgId ?? null);
      await refreshRole();
      if (!result.nextActiveOrgId) {
        nav('/dashboard/profile');
      }
    } catch (e: any) {
      setLeaveOrgError(e?.message || 'Failed to leave organization');
    } finally {
      setLeaveOrgSubmitting(false);
    }
  }, [
    authOrgId,
    businessProfile?.isPrimaryAccountHolder,
    leaveOrgSubmitting,
    nav,
    orgId,
    refreshRole,
  ]);

  const primaryBusinessPendingProposal = useMemo(
    () => pendingServiceProposals[0] ?? null,
    [pendingServiceProposals],
  );
  const hasServiceLevelContract = hasAttachedServiceContract;
  const showTransferContractSidebarPanel = Boolean(
    businessTransferableContracts.length && !hasServiceLevelContract,
  );
  const showIndividualContractManagerPanel = Boolean(
    businessProfile?.organizationType === 'individual'
    && businessProfile?.canEdit
    && manageableContracts.length,
  );
  const sidebarCurrentService = useMemo(
    () => (!showTransferContractSidebarPanel ? currentServices[0] ?? null : null),
    [currentServices, showTransferContractSidebarPanel],
  );
  const remainingCurrentServices = useMemo(
    () => (sidebarCurrentService ? currentServices.slice(1) : currentServices),
    [currentServices, sidebarCurrentService],
  );
  const showServiceContractsSection = Boolean(
    businessProfile?.organizationType === 'business'
    && businessProfile?.isPrimaryAccountHolder,
  );
  const showBusinessPageSkeleton = Boolean(
    user
    && (
      loading
      || (activeWorkspaceOrgId && loadedOrgId !== activeWorkspaceOrgId)
    ),
  );
  const serviceContractsLeadTitle = hasServiceLevelContract ? 'Explore additional services' : 'Explore our services';
  const serviceContractsLeadCopy = hasServiceLevelContract
    ? 'You already have service coverage in motion. Open the services workspace to explore additional offerings, upgrades, or new proposals for this business.'
    : 'No service contract is attached to this business yet. Explore the services workspace to review available offerings and start a purchase when you are ready.';
  const serviceContractsLeadButtonLabel = hasServiceLevelContract ? 'Explore services' : 'Purchase a service';

  const renderServiceContractCard = (
    service: ServiceWorkspaceCurrentService,
    keyOverride?: string,
  ) => {
    const category = normalizeServiceContractCategory(
      service.serviceCode || service.serviceName,
    );
    const theme = getServiceContractTheme(category);
    const title = String(service.serviceName ?? service.serviceCode ?? 'Service').trim() || 'Service';
    const offeringNames = getCurrentContractOfferingNames(service);
    const currentTier = resolveCurrentContractTier(service, serviceCatalog);
    const hoursLabel =
      formatServiceContractHours(
        service.contractedMonthlyMinutesMin,
        service.contractedMonthlyMinutesMax,
      )
      || readTierHoursSummary(currentTier);
    const meetingsLabel = readTierMeetingsSummary(currentTier);
    const visibleOfferings = offeringNames.slice(0, 4);
    const remainingOfferings = offeringNames.length - visibleOfferings.length;

    return (
      <ServiceContractCard
        key={keyOverride ?? service.id}
        $gradient={theme.gradient}
        $glow={theme.glow}
      >
        <ServiceContractOverlay />
        <ServiceContractInner>
          <ServiceContractBody>
            <ServiceContractHead>
              <ServiceContractEyebrow>
                {formatServiceFamilyLabel(service)}
              </ServiceContractEyebrow>
              <ServiceContractBadge>Current contract</ServiceContractBadge>
            </ServiceContractHead>

            <ServiceContractTitle>{title}</ServiceContractTitle>

            <ServiceContractStack>
              <ServiceContractItem>
                <ServiceContractLabel>Service</ServiceContractLabel>
                <ServiceContractValue>{title}</ServiceContractValue>
              </ServiceContractItem>

              <ServiceContractItem>
                <ServiceContractLabel>Tier</ServiceContractLabel>
                <ServiceContractValue>
                  {String(service.tierName ?? 'Current tier').trim() || 'Current tier'}
                </ServiceContractValue>
              </ServiceContractItem>

              {hoursLabel ? (
                <ServiceContractItem>
                  <ServiceContractLabel>Included hours</ServiceContractLabel>
                  <ServiceContractHours>{hoursLabel}</ServiceContractHours>
                </ServiceContractItem>
              ) : null}

              {meetingsLabel ? (
                <ServiceContractItem>
                  <ServiceContractLabel>Client success meetings</ServiceContractLabel>
                  <ServiceContractValue>{meetingsLabel}</ServiceContractValue>
                </ServiceContractItem>
              ) : null}

              <ServiceContractItem>
                <ServiceContractLabel>Selected offerings</ServiceContractLabel>
                {visibleOfferings.length ? (
                  <>
                    <ServiceContractList>
                      {visibleOfferings.map((offering) => (
                        <li key={`${service.id}-${offering}`}>{offering}</li>
                      ))}
                    </ServiceContractList>
                    {remainingOfferings > 0 ? (
                      <ServiceContractOverflow>
                        +{remainingOfferings} more offering{remainingOfferings === 1 ? '' : 's'}
                      </ServiceContractOverflow>
                    ) : null}
                  </>
                ) : (
                  <ServiceContractValue>No offerings recorded on this contract.</ServiceContractValue>
                )}
              </ServiceContractItem>
            </ServiceContractStack>
          </ServiceContractBody>

          <ServiceContractFooter $footer={theme.footer}>
            <ServiceContractFooterTitle>Current plan</ServiceContractFooterTitle>
            <ServiceContractFooterCopy>
              Active service contract attached to this business.
            </ServiceContractFooterCopy>
          </ServiceContractFooter>
        </ServiceContractInner>
      </ServiceContractCard>
    );
  };

  return (
    <Wrap>
      <PageShell>
        {organizations.length > 1 ? (
          <OrgSwitcherBar>
            <OrgSwitcherMeta>
              <OrgSwitcherLabel>Active organization</OrgSwitcherLabel>
              <OrgSwitcherValue>
                {`${activeOrganization?.businessName
                  || activeOrganization?.orgName
                  || 'Organization'} • ${formatOrganizationTypeLabel(activeOrganization?.organizationType)}`}
              </OrgSwitcherValue>
            </OrgSwitcherMeta>
            <OrgSwitcherSelect
              value={orgId ?? authOrgId ?? ''}
              onChange={(event) => {
                void handleOrganizationSwitch(event.target.value);
              }}
              disabled={orgSwitching || loading}
              aria-label="Switch organization context"
            >
              {organizations.map((organization) => (
                <option key={organization.orgId} value={organization.orgId}>
                  {`${organization.businessName || organization.orgName} • ${formatOrganizationTypeLabel(organization.organizationType)}`}
                </option>
              ))}
            </OrgSwitcherSelect>
          </OrgSwitcherBar>
        ) : null}
        {pendingOrganizationInvites.length ? (
          <InviteBannerStack>
            {pendingOrganizationInvites.map((invite) => (
              <InviteBanner key={`${invite.orgId}:${invite.token}`}>
                <InviteBannerConfetti />
                <InviteBannerText>
                  <InviteBannerTitle>
                    {`You have been invited to become ${formatOrgInviteRoleLabel(invite.invitedRole).toLowerCase()} of ${invite.businessName || 'this organization'}.`}
                  </InviteBannerTitle>
                  <InviteBannerCopy>
                    Review the organization details before you accept or reject the invite.
                  </InviteBannerCopy>
                </InviteBannerText>
                <InviteBannerActions>
                  <InviteBannerButton
                    type="button"
                    onClick={() => {
                      void openInvitePreview(invite);
                    }}
                  >
                    View invite
                  </InviteBannerButton>
                </InviteBannerActions>
              </InviteBanner>
            ))}
          </InviteBannerStack>
        ) : null}
        {error ? <ErrorText>{error}</ErrorText> : null}
        {businessNotice ? <Muted>{businessNotice}</Muted> : null}

        {showBusinessPageSkeleton ? (
          <>
            <Section>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.75rem', flexWrap: 'wrap' }}>
                <div>
                  <BusinessSectionTitle>Business profile</BusinessSectionTitle>
                  <Muted style={{ margin: '.2rem 0 0' }}>
                    Manage the organization details used across services, tickets, and member collaboration.
                  </Muted>
                </div>
              </div>
              <BusinessPageSkeletonPanel>
                <BusinessProfileServicesLayout>
                  <BusinessProfileMainColumn>
                    <BusinessProfileRow>
                      <BusinessLogoPane>
                        <Skeleton $width="132px" $height="132px" $radius="14px" />
                      </BusinessLogoPane>
                      <BusinessFormPane>
                        <TwoCol>
                          <Skeleton $height="44px" $radius="10px" />
                          <Skeleton $height="44px" $radius="10px" />
                          <Skeleton $height="44px" $radius="10px" />
                          <Skeleton $height="44px" $radius="10px" />
                          <Skeleton $height="44px" $radius="10px" />
                          <Skeleton $height="44px" $radius="10px" />
                        </TwoCol>
                        <Skeleton $height="44px" $radius="10px" />
                      </BusinessFormPane>
                    </BusinessProfileRow>
                  </BusinessProfileMainColumn>
                  <BusinessProfileSidebarColumn>
                    <BusinessPageSkeletonPanel>
                      <Skeleton $width="64%" $height="22px" />
                      <Skeleton $width="100%" $height="220px" $radius="20px" />
                    </BusinessPageSkeletonPanel>
                    <BusinessPageSkeletonPanel>
                      <Skeleton $width="56%" $height="24px" />
                      <Skeleton $width="92%" />
                      <Skeleton $width="86%" />
                      <Skeleton $width="180px" $height="44px" $radius="999px" />
                    </BusinessPageSkeletonPanel>
                  </BusinessProfileSidebarColumn>
                </BusinessProfileServicesLayout>
              </BusinessPageSkeletonPanel>
            </Section>

            <Section>
              <BusinessPageSkeletonPanel>
                <BusinessPageSkeletonRows>
                  {Array.from({ length: 3 }, (_, index) => (
                    <BusinessPageSkeletonMemberRow key={`team-skeleton-${index}`}>
                      <Skeleton $width="44px" $height="44px" $radius="999px" />
                      <div style={{ display: 'grid', gap: '.35rem', minWidth: 0 }}>
                        <Skeleton $width={index === 0 ? '42%' : index === 1 ? '54%' : '48%'} />
                        <Skeleton $width={index === 0 ? '26%' : '22%'} $height="12px" />
                      </div>
                    </BusinessPageSkeletonMemberRow>
                  ))}
                </BusinessPageSkeletonRows>
              </BusinessPageSkeletonPanel>
            </Section>
            <Section>
              <SectionTitle>Danger zone</SectionTitle>
              <DangerCard>
                <Skeleton $width="42%" $height="20px" />
                <Skeleton $width="94%" />
                <Skeleton $width="82%" />
                <DangerActions>
                  <Skeleton $width="160px" $height="40px" $radius="999px" />
                  <Skeleton $width="170px" $height="40px" $radius="999px" />
                </DangerActions>
              </DangerCard>
            </Section>
          </>
        ) : (
        <>
        <Section>
          {loading && !businessProfile ? (
            <Muted>Loading business profile...</Muted>
          ) : showBusinessSetupPrompt ? (
            <BusinessSetupLayout>
              <BusinessSetupLead>
                <BusinessSetupLeadTitle>
                  {showIndividualContractManagerPanel
                    ? 'Manage your service contracts'
                    : 'Build your business profile'}
                </BusinessSetupLeadTitle>

                <BusinessSetupPoints>
                  {showIndividualContractManagerPanel ? (
                    <>
                      <BusinessSetupPoint>
                        <strong>See contract ownership clearly:</strong> review which organization is currently holding each active service contract.
                      </BusinessSetupPoint>
                      <BusinessSetupPoint>
                        <strong>Choose the right workspace:</strong> move a contract to the individual or business organization where you want that service to live.
                      </BusinessSetupPoint>
                      <BusinessSetupPoint>
                        <strong>Keep service access aligned:</strong> make sure each contract is attached to the workspace that should receive proposals, billing context, and delivery collaboration.
                      </BusinessSetupPoint>
                    </>
                  ) : (
                    <>
                      <BusinessSetupPoint>
                        <strong>Unlock a ready workspace:</strong> establish the business
                        details used across services, projects, tickets, and collaboration.
                      </BusinessSetupPoint>
                      <BusinessSetupPoint>
                        <strong>Give your team context:</strong> keep members aligned on the
                        correct business name, contact details, and service destination.
                      </BusinessSetupPoint>
                      <BusinessSetupPoint>
                        <strong>Look established from the start:</strong> create a clean
                        organization profile that is ready for growth, proposals, and account
                        management.
                      </BusinessSetupPoint>
                    </>
                  )}
                </BusinessSetupPoints>
              </BusinessSetupLead>
              {showCreateBusinessProfileCard || showIndividualContractManagerPanel ? (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {showCreateBusinessProfileCard ? (
                    <BusinessSetupCard>
                      <div style={{ display: 'grid', gap: '.35rem' }}>
                        <Name>Create business profile</Name>
                        <Muted style={{ margin: 0 }}>
                          Add the core organization details your teams and service workspace rely on.
                        </Muted>
                      </div>
                      {businessProfile?.canEdit ? (
                        <div>
                          <BusinessPrimaryButton
                            type="button"
                            onClick={() => {
                              setBusinessEditMode(true);
                              setBusinessNotice(null);
                              setError(null);
                            }}
                          >
                            Create business profile
                          </BusinessPrimaryButton>
                        </div>
                      ) : (
                        <Muted style={{ margin: 0 }}>
                          Only the primary account holder can build or edit the business profile.
                        </Muted>
                      )}
                    </BusinessSetupCard>
                  ) : null}

                  {showIndividualContractManagerPanel ? (
                    <WorkspaceContractLocationPanel>
                      <WorkspaceContractLocationHeader>
                        <WorkspaceContractLocationTitle>Current service contract</WorkspaceContractLocationTitle>
                        
                        <WorkspaceContractLocationField>
                          <WorkspaceContractLocationLabel>Service contract</WorkspaceContractLocationLabel>
                          <Select
                            value={selectedTransferContractId}
                            onChange={(event) => setSelectedTransferContractId(event.target.value)}
                            disabled={transferContractSubmitting}
                          >
                            {manageableContracts.map((contract) => {
                              const attachedName =
                                contract.attachedOrganization?.businessName?.trim()
                                || contract.attachedOrganization?.name?.trim()
                                || 'your individual workspace';
                              return (
                                <option key={contract.id} value={contract.id}>
                                  {`${String(contract.serviceName ?? contract.serviceCode ?? 'Service').trim() || 'Service'} / ${String(contract.tierName ?? 'Tier').trim() || 'Tier'} - ${attachedName}`}
                                </option>
                              );
                            })}
                          </Select>
                        </WorkspaceContractLocationField>
                        <WorkspaceContractLocationAttachedRow>
                          <WorkspaceContractLocationOrgAvatar>
                            {selectedTransferContract?.attachedOrganization?.logoUrl ? (
                              <img
                                src={selectedTransferContract.attachedOrganization.logoUrl}
                                alt={selectedTransferContractLocationLabel}
                              />
                            ) : (
                              initialsFromLabel(selectedTransferContractLocationLabel)
                            )}
                          </WorkspaceContractLocationOrgAvatar>
                          <WorkspaceContractLocationOrgText>
                            <WorkspaceContractLocationMeta>
                              Currently attached
                            </WorkspaceContractLocationMeta>
                            <WorkspaceContractLocationOrgName>
                              {selectedTransferContractLocationLabel}
                            </WorkspaceContractLocationOrgName>
                          </WorkspaceContractLocationOrgText>
                        </WorkspaceContractLocationAttachedRow>
                      </WorkspaceContractLocationHeader>
                      <WorkspaceContractLocationControls>
                        <WorkspaceContractLocationField>
                          <WorkspaceContractLocationLabel>Transfer to organization</WorkspaceContractLocationLabel>
                          <Select
                            value={selectedTransferTargetOrgId}
                            onChange={(event) => setSelectedTransferTargetOrgId(event.target.value)}
                            disabled={transferContractSubmitting || !availableTransferTargetOrganizations.length}
                          >
                            {availableTransferTargetOrganizations.length ? (
                              availableTransferTargetOrganizations.map((organization) => (
                                <option key={organization.orgId} value={organization.orgId}>
                                  {`${organization.businessName || organization.orgName} - ${formatOrganizationTypeLabel(organization.organizationType)}`}
                                </option>
                              ))
                            ) : (
                              <option value="" disabled>
                                No other eligible organization
                              </option>
                            )}
                          </Select>
                        </WorkspaceContractLocationField>
                        <ServiceGhostButton
                          type="button"
                          onClick={() =>
                            void handleTransferServiceContract({
                              targetOrgId: selectedTransferTargetOrgId,
                              successNotice: `Service contract transferred to ${selectedTransferTargetOrganization?.businessName || selectedTransferTargetOrganization?.orgName || 'the selected organization'}.`,
                            })
                          }
                          disabled={
                            !selectedTransferContractId
                            || !selectedTransferTargetOrgId
                            || transferContractSubmitting
                            || selectedTransferTargetOrgId === selectedTransferContractAttachedOrgId
                          }
                        >
                          {transferContractSubmitting ? 'Transferring...' : 'Transfer contract'}
                        </ServiceGhostButton>
                      </WorkspaceContractLocationControls>
                    </WorkspaceContractLocationPanel>
                  ) : null}
                </div>
              ) : null}
            </BusinessSetupLayout>
          ) : businessProfile ? (
            <BusinessProfileServicesLayout>
              <BusinessProfileMainColumn>
                <BusinessProfileHeaderRow>
                  <BusinessProfileHeaderText>
                    <BusinessSectionTitle>Business profile</BusinessSectionTitle>
                    <Muted style={{ margin: 0 }}>
                      Manage the organization details used across services, tickets, and member collaboration.
                    </Muted>
                  </BusinessProfileHeaderText>
                  {businessProfile.canEdit && hasBusinessProfile && !businessEditMode && !businessFormDirty ? (
                    <BusinessHeaderEditButton
                      type="button"
                      aria-label="Edit business profile"
                      onClick={() => {
                        setBusinessEditMode(true);
                        setBusinessNotice(null);
                        setError(null);
                      }}
                    >
                      <Pencil size={16} />
                    </BusinessHeaderEditButton>
                  ) : null}
                </BusinessProfileHeaderRow>

                <BusinessProfileRow>
                  <BusinessLogoPane>
                    <BusinessLogoMobileRow>
                      <BusinessLogoFrame>
                        <BusinessLogo>
                          {businessForm.logoURL ? (
                            <img src={businessForm.logoURL} alt="Business logo" />
                          ) : (
                            initialsFromLabel(
                              businessForm.businessName
                                || businessProfile.orgName
                                || activeOrganization?.businessName
                                || activeOrganization?.orgName
                                || 'Business',
                            )
                          )}
                        </BusinessLogo>
                        <LogoEditButton
                          type="button"
                          onClick={triggerLogoUpload}
                          disabled={!businessProfile.canEdit || logoUploading}
                        >
                          {logoUploading ? '...' : businessForm.logoURL ? 'Edit' : 'Add'}
                        </LogoEditButton>
                      </BusinessLogoFrame>
                      {businessProfile.canEdit && hasBusinessProfile && !businessEditMode && !businessFormDirty ? (
                        <BusinessLogoMobileEditButton
                          type="button"
                          aria-label="Edit business profile"
                          onClick={() => {
                            setBusinessEditMode(true);
                            setBusinessNotice(null);
                            setError(null);
                          }}
                        >
                          <Pencil size={14} />
                          Edit profile
                        </BusinessLogoMobileEditButton>
                      ) : null}
                    </BusinessLogoMobileRow>
                    <HiddenFileInput
                      ref={logoFileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(event) => {
                        void handleLogoFileChange(event.target.files?.[0] ?? null);
                      }}
                    />
                  </BusinessLogoPane>

                  <BusinessFormPane>
                    <TwoCol>
                      <Input
                        $editing={Boolean(businessProfile.canEdit && businessEditMode)}
                        value={businessForm.businessName}
                        onChange={(event) =>
                          setBusinessForm((prev) => ({
                            ...prev,
                            businessName: event.target.value,
                          }))
                        }
                        readOnly={!businessEditMode || !businessProfile.canEdit}
                        disabled={!businessProfile.canEdit}
                        placeholder="Business name *"
                      />
                      <Input
                        $editing={Boolean(businessProfile.canEdit && businessEditMode)}
                        value={businessForm.contactName}
                        onChange={(event) =>
                          setBusinessForm((prev) => ({
                            ...prev,
                            contactName: event.target.value,
                          }))
                        }
                        readOnly={!businessEditMode || !businessProfile.canEdit}
                        disabled={!businessProfile.canEdit}
                        placeholder="Industry"
                      />
                      <Input
                        $editing={Boolean(businessProfile.canEdit && businessEditMode)}
                        value={businessForm.contactEmail}
                        onChange={(event) =>
                          setBusinessForm((prev) => ({
                            ...prev,
                            contactEmail: event.target.value,
                          }))
                        }
                        readOnly={!businessEditMode || !businessProfile.canEdit}
                        disabled={!businessProfile.canEdit}
                        placeholder="Business contact email *"
                        type="email"
                      />
                      <Input
                        $editing={Boolean(businessProfile.canEdit && businessEditMode)}
                        value={businessForm.contactPhone}
                        onChange={(event) =>
                          setBusinessForm((prev) => ({
                            ...prev,
                            contactPhone: formatUsPhone(event.target.value),
                          }))
                        }
                        readOnly={!businessEditMode || !businessProfile.canEdit}
                        disabled={!businessProfile.canEdit}
                        placeholder="Business contact phone *"
                        inputMode="tel"
                        maxLength={14}
                      />
                      <Input
                        $editing={Boolean(businessProfile.canEdit && businessEditMode)}
                        value={businessForm.website}
                        onChange={(event) =>
                          setBusinessForm((prev) => ({
                            ...prev,
                            website: event.target.value,
                          }))
                        }
                        readOnly={!businessEditMode || !businessProfile.canEdit}
                        disabled={!businessProfile.canEdit}
                        placeholder="Website"
                      />
                      <Input
                        $editing={Boolean(businessProfile.canEdit && businessEditMode)}
                        value={businessForm.address}
                        onChange={(event) =>
                          setBusinessForm((prev) => ({
                            ...prev,
                            address: event.target.value,
                          }))
                        }
                        readOnly={!businessEditMode || !businessProfile.canEdit}
                        disabled={!businessProfile.canEdit}
                        placeholder="Business address"
                      />
                    </TwoCol>
                  </BusinessFormPane>
                </BusinessProfileRow>

                {businessProfile.canEdit && businessFormDirty ? (
                  <BusinessFormFooter>
                    {hasBusinessProfile ? (
                      <Button type="button" onClick={() => void saveBusinessProfile()} disabled={businessSaving}>
                        {businessSaving ? 'Saving...' : 'Save business profile'}
                      </Button>
                    ) : (
                      <BusinessPrimaryButton
                        type="button"
                        onClick={() => void saveBusinessProfile()}
                        disabled={businessSaving}
                      >
                        {businessSaving ? 'Creating...' : 'Create business profile'}
                      </BusinessPrimaryButton>
                    )}
                  </BusinessFormFooter>
                ) : !businessProfile.canEdit ? (
                  <Muted style={{ margin: 0 }}>
                    Only the primary account holder can edit business profile fields.
                  </Muted>
                ) : null}
              </BusinessProfileMainColumn>

              {showServiceContractsSection ? (
                <BusinessProfileSidebarColumn>
                  {serviceContractsUnavailable ? (
                    <Muted style={{ margin: 0 }}>Service contracts are unavailable right now.</Muted>
                  ) : (
                    <>
                      {showTransferContractSidebarPanel ? (
                        <ServiceTransferSidebarPanel>
                          <ServiceTransferSidebarTop>
                            <ServiceTransferSidebarLottieWrap aria-hidden="true">
                              <Lottie animationData={circuitAnimation} loop autoplay />
                            </ServiceTransferSidebarLottieWrap>
                            <ServiceTransferSidebarTopOverlay />
                            <ServiceTransferSidebarText>
                              <ServiceTransferSidebarTitle>Transfer existing service contract</ServiceTransferSidebarTitle>
                              <ServiceTransferSidebarCopy>
                                Move one of your active service contracts onto this business organization.
                              </ServiceTransferSidebarCopy>
                            </ServiceTransferSidebarText>
                          </ServiceTransferSidebarTop>
                          <ServiceTransferSidebarBottom>
                            <Select
                              value={selectedTransferContractId}
                              onChange={(event) => setSelectedTransferContractId(event.target.value)}
                              disabled={transferContractSubmitting}
                            >
                              {businessTransferableContracts.map((contract) => (
                                <option key={contract.id} value={contract.id}>
                                  {`${String(contract.serviceName ?? contract.serviceCode ?? 'Service').trim() || 'Service'} / ${String(contract.tierName ?? 'Tier').trim() || 'Tier'}`}
                                </option>
                              ))}
                            </Select>
                            <ServiceGhostButton
                              type="button"
                              onClick={() => void handleTransferServiceContract()}
                              disabled={!selectedTransferContractId || transferContractSubmitting}
                            >
                              {transferContractSubmitting ? 'Transferring...' : 'Transfer contract'}
                            </ServiceGhostButton>
                          </ServiceTransferSidebarBottom>
                        </ServiceTransferSidebarPanel>
                      ) : sidebarCurrentService ? (
                        renderServiceContractCard(
                          sidebarCurrentService,
                          `${sidebarCurrentService.id}-sidebar`,
                        )
                      ) : null}

                      <BusinessProfileServicesLead>
                        <ServiceContractsLeadTitle>{serviceContractsLeadTitle}</ServiceContractsLeadTitle>
                        <ServiceContractsLeadCopy>
                          {serviceContractsLeadCopy}
                        </ServiceContractsLeadCopy>
                        <ServiceContractsLeadActions>
                          <ServicePrimaryButton
                            type="button"
                            onClick={() => nav('/dashboard/services')}
                          >
                            {serviceContractsLeadButtonLabel}
                          </ServicePrimaryButton>
                        </ServiceContractsLeadActions>
                      </BusinessProfileServicesLead>

                      {primaryBusinessPendingProposal ? (
                        <ServiceSidebarPanel>
                          <ServiceSidebarPanelTitle>You have a proposal waiting</ServiceSidebarPanelTitle>
                          {primaryBusinessPendingProposal.summary ? (
                            <ServiceSidebarPanelCopy>
                              {primaryBusinessPendingProposal.summary}
                            </ServiceSidebarPanelCopy>
                          ) : null}
                          <ServiceSidebarPanelControls>
                            <ServiceGhostButton
                              type="button"
                              onClick={() =>
                                nav(`/dashboard/services?proposalId=${encodeURIComponent(primaryBusinessPendingProposal.id)}`)
                              }
                            >
                              View proposal
                            </ServiceGhostButton>
                          </ServiceSidebarPanelControls>
                        </ServiceSidebarPanel>
                      ) : null}
                    </>
                  )}
                </BusinessProfileSidebarColumn>
              ) : null}
            </BusinessProfileServicesLayout>
          ) : (
            <Muted>Business profile is unavailable right now.</Muted>
          )}
        </Section>

        {showServiceContractsSection && remainingCurrentServices.length ? (
        <Section>
            <ServiceContractsGrid>
              {remainingCurrentServices.map((service) =>
                renderServiceContractCard(service),
              )}
            </ServiceContractsGrid>
        </Section>
        ) : null}

        {businessProfile?.organizationType === 'business' ? (
          <Section>
            <OrganizationTeamPanel onInviteClick={openInviteModal} />
          </Section>
        ) : null}

        {businessProfile?.organizationType === 'business' ? (
          <Section>
            <SectionTitle>Danger zone</SectionTitle>
            <DangerCard>
              <DangerTitle>Delete or leave organization</DangerTitle>
              <Muted style={{ margin: 0 }}>
                Primary account holders can permanently delete the business organization. Organization members can leave the business organization and lose access to its workspace.
              </Muted>
              <DangerActions>
                <DangerGhostButton
                  type="button"
                  onClick={() => {
                    setLeaveOrgError(null);
                    setLeaveOrgModalOpen(true);
                  }}
                  disabled={businessProfile.isPrimaryAccountHolder || leaveOrgSubmitting}
                >
                  Leave organization
                </DangerGhostButton>
                <DangerButton
                  type="button"
                  onClick={() => setDeleteOrgModalOpen(true)}
                  disabled={!businessProfile.isPrimaryAccountHolder || deleteOrgSubmitting}
                >
                  Delete organization
                </DangerButton>
              </DangerActions>
              {businessProfile.isPrimaryAccountHolder ? (
                <Muted style={{ margin: 0 }}>
                  Transfer primary account holder access before leaving this organization.
                </Muted>
              ) : (
                <Muted style={{ margin: 0 }}>
                  Only the primary account holder can delete this organization.
                </Muted>
              )}
            </DangerCard>
          </Section>
        ) : null}
        </>
        )}
      </PageShell>

      <InviteTeammateModal
        open={inviteModalOpen}
        onClose={closeInviteModal}
        orgId={orgId ?? authOrgId ?? ''}
      />

      <ModalBackdrop
        type="button"
        aria-label="Close organization invite modal"
        $open={invitePreviewOpen}
        onClick={closeInvitePreview}
      />
      <InvitePreviewModal
        $open={invitePreviewOpen}
        role="dialog"
        aria-modal="true"
        aria-labelledby="organization-invite-preview-title"
      >
        <InvitePreviewHead>
          <ModalTitle id="organization-invite-preview-title">
            {activeInvitePreview?.businessName || selectedInviteMeta?.businessName || 'Organization invite'}
          </ModalTitle>
        </InvitePreviewHead>
        <InvitePreviewBody>
          {invitePreviewLoading ? (
            <ModalText>Loading invite details...</ModalText>
          ) : invitePreviewError ? (
            <ErrorText style={{ marginTop: 0 }}>{invitePreviewError}</ErrorText>
          ) : activeInvitePreview ? (
            <>
              <ModalText>
                {`You have been invited to join ${activeInvitePreview.businessName} as ${formatOrgInviteRoleLabel(activeInvitePreview.invitedRole).toLowerCase()}.`}
              </ModalText>
              <InvitePreviewCard>
                <ModalLabel>Invited by</ModalLabel>
                <InviteIdentityRow>
                  <PortalAvatar
                    size={40}
                    name={
                      activeInvitePreview.invitedByName
                      || activeInvitePreview.primaryAccountHolderName
                      || 'Organization owner'
                    }
                    photoURL={activeInvitePreview.invitedByPhotoURL}
                    disableNavigation
                    implicitSelfFallback={false}
                  />
                  <InviteIdentityMeta>
                    <InviteIdentityName>
                      {activeInvitePreview.invitedByName
                        || activeInvitePreview.primaryAccountHolderName
                        || 'Organization owner'}
                    </InviteIdentityName>
                  </InviteIdentityMeta>
                </InviteIdentityRow>
              </InvitePreviewCard>
              <InvitePreviewCard>
                <ModalLabel>Organization type</ModalLabel>
                <ModalValue>{formatOrganizationTypeLabel(activeInvitePreview.organizationType)}</ModalValue>
              </InvitePreviewCard>
              {activeInvitePreview.industry ? (
                <InvitePreviewCard>
                  <ModalLabel>Industry</ModalLabel>
                  <ModalValue>{activeInvitePreview.industry}</ModalValue>
                </InvitePreviewCard>
              ) : null}
              {(activeInvitePreview.contactEmail || activeInvitePreview.contactPhone || activeInvitePreview.website || activeInvitePreview.address) ? (
                <InvitePreviewCard>
                  <ModalLabel>Business profile</ModalLabel>
                  {activeInvitePreview.contactEmail ? <ModalValue>{activeInvitePreview.contactEmail}</ModalValue> : null}
                  {activeInvitePreview.contactPhone ? <ModalValue>{formatUsPhone(activeInvitePreview.contactPhone)}</ModalValue> : null}
                  {activeInvitePreview.website ? <ModalValue>{activeInvitePreview.website}</ModalValue> : null}
                  {activeInvitePreview.address ? <ModalValue>{activeInvitePreview.address}</ModalValue> : null}
                </InvitePreviewCard>
              ) : null}
              <InvitePreviewCard>
                <ModalLabel>Members</ModalLabel>
                {activeInvitePreview.members.length ? (
                  <InviteMembersList>
                    {activeInvitePreview.members.map((member) => (
                      <InviteMemberRow key={member.uid}>
                        <InviteMemberMeta>
                          <InviteMemberName>{formatInviteMemberName(member)}</InviteMemberName>
                          <InviteMemberEmail>{member.email || member.uid}</InviteMemberEmail>
                        </InviteMemberMeta>
                        <InviteRolePill>
                          {member.isPrimaryAccountHolder || member.role === 'owner'
                            ? 'Primary account holder'
                            : 'Member'}
                        </InviteRolePill>
                      </InviteMemberRow>
                    ))}
                  </InviteMembersList>
                ) : (
                  <ModalText>No members are attached to this organization yet.</ModalText>
                )}
              </InvitePreviewCard>
            </>
          ) : (
            <ModalText>Invite details are not available.</ModalText>
          )}
          {inviteDecisionError ? <ErrorText style={{ marginTop: 0 }}>{inviteDecisionError}</ErrorText> : null}
        </InvitePreviewBody>
        <InvitePreviewActions>
          <QuietBtn
            type="button"
            onClick={() => {
              void handleInviteDecision('reject');
            }}
            disabled={inviteDecisionPending !== null || !selectedInviteMeta}
          >
            {inviteDecisionPending === 'reject' ? 'Rejecting...' : 'Reject invite'}
          </QuietBtn>
          <InviteDecisionButton
            type="button"
            onClick={() => {
              void handleInviteDecision('accept');
            }}
            disabled={inviteDecisionPending !== null || !selectedInviteMeta}
          >
            {inviteDecisionPending === 'accept' ? 'Accepting...' : 'Accept invite'}
          </InviteDecisionButton>
        </InvitePreviewActions>
      </InvitePreviewModal>

      <ModalBackdrop
        type="button"
        aria-label="Close service transfer prompt"
        $open={Boolean(serviceTransferPrompt)}
        onClick={() => {
          if (transferContractSubmitting) return;
          closeServiceTransferPrompt();
        }}
      />
      <ServiceTransferPromptModal
        $open={Boolean(serviceTransferPrompt)}
        role="dialog"
        aria-modal="true"
        aria-label={`Make ${serviceTransferPromptBusinessName} your primary workspace`}
      >
        <ServiceTransferPromptHero>
          <ServiceTransferPromptHeroOverlay />
          <ServiceTransferPromptLottieWrap aria-hidden="true">
            <Lottie animationData={circuitAnimation} loop autoplay />
          </ServiceTransferPromptLottieWrap>
          <ServiceTransferPromptHeroTitle>
            Make {serviceTransferPromptBusinessName} your primary workspace
          </ServiceTransferPromptHeroTitle>
        </ServiceTransferPromptHero>
        <ServiceTransferPromptBottom>
          <ServiceTransferPromptBody>
            You just became the primary account holder for{' '}
            <strong>{serviceTransferPromptBusinessName}</strong>. Move your active
            service contract onto this business organization now if you want it to
            become your primary service workspace.
          </ServiceTransferPromptBody>

          <ServiceTransferPromptField>
            <ModalLabel>Service contract</ModalLabel>
            <Select
              value={selectedTransferContractId}
              onChange={(event) => setSelectedTransferContractId(event.target.value)}
              disabled={transferContractSubmitting || !businessTransferableContracts.length}
            >
              {businessTransferableContracts.map((contract) => (
                <option key={contract.id} value={contract.id}>
                  {`${String(contract.serviceName ?? contract.serviceCode ?? 'Service').trim() || 'Service'} / ${String(contract.tierName ?? 'Tier').trim() || 'Tier'}`}
                </option>
              ))}
            </Select>
            
          </ServiceTransferPromptField>

          {serviceTransferPromptError ? (
            <ErrorText style={{ marginTop: 0 }}>{serviceTransferPromptError}</ErrorText>
          ) : null}

          <ServiceTransferPromptActions>
          <ServicePrimaryButton
            type="button"
            onClick={() => void handleConfirmServiceTransferPrompt()}
            disabled={!selectedTransferContractId || transferContractSubmitting}
          >
            {transferContractSubmitting
              ? 'Transferring...'
              : `make ${serviceTransferPromptBusinessName} the primary organization`}
          </ServicePrimaryButton>
          <ServiceTransferPromptDismiss
            type="button"
            onClick={closeServiceTransferPrompt}
            disabled={transferContractSubmitting}
          >
            Not now
          </ServiceTransferPromptDismiss>
          </ServiceTransferPromptActions>
        </ServiceTransferPromptBottom>
      </ServiceTransferPromptModal>

      <ModalBackdrop
        type="button"
        aria-label="Close transfer confirmation modal"
        $open={transferModalOpen}
        onClick={() => setTransferModalOpen(false)}
      />
      <Modal
        $open={transferModalOpen}
        role="dialog"
        aria-modal="true"
        aria-labelledby="primary-transfer-confirm-title"
      >
        <ModalHead>
          <ModalTitle id="primary-transfer-confirm-title">Transfer Primary Account Holder</ModalTitle>
        </ModalHead>
        <ModalBody>
          <ModalText>
            Transfer primary account holder access to{' '}
            <strong>{transferTarget?.name || 'this member'}</strong>.
          </ModalText>

          <ModalCard>
            <ModalLabel>Active service status</ModalLabel>
            <ModalValue>
              {isSubscriptionVerificationUnavailable
                ? 'Unable to verify'
                : hasActiveService
                  ? 'Active subscription detected'
                  : 'No active subscription'}
            </ModalValue>
          </ModalCard>

          <ModalCard>
            <ModalLabel>Payment method requirement</ModalLabel>
            <ModalValue>
              {requiresPaymentMethodForTransfer
                ? 'Required for transfer'
                : 'Optional for transfer'}
            </ModalValue>
            <ModalText style={{ marginTop: '.1rem' }}>
              {requiresPaymentMethodForTransfer
                ? 'Because an active service exists, one payment method must remain on file.'
                : 'No active service is detected, so payment method is not required for ownership handoff.'}
            </ModalText>
          </ModalCard>

          {requiresPaymentMethodForTransfer ? (
            <ModalCard>
              <ModalLabel>Payment method on file</ModalLabel>
              {hasPaymentMethodOnFile ? (
                <>
                  <Select
                    value={transferPaymentMethodId}
                    onChange={(e) => setTransferPaymentMethodId(e.target.value)}
                  >
                    {paymentMethods.map((method) => (
                      <option key={method.id} value={method.id}>
                        {formatPaymentMethod(method)}
                        {method.isDefault ? ' (Default)' : ''}
                      </option>
                    ))}
                  </Select>
                  <ModalText>{formatPaymentMethod(selectedTransferPaymentMethod)}</ModalText>
                </>
              ) : (
                <ModalText>No payment method on file. Add one in Billing before transfer.</ModalText>
              )}
            </ModalCard>
          ) : null}

          {transferModalError ? <ErrorText style={{ marginTop: 0 }}>{transferModalError}</ErrorText> : null}
        </ModalBody>
        <ModalActions>
          <QuietBtn type="button" onClick={() => setTransferModalOpen(false)}>
            Cancel
          </QuietBtn>
          <Button
            type="button"
            onClick={() => {
              setTransferModalOpen(false);
              nav('/dashboard/billing');
            }}
          >
            Change payment method
          </Button>
          <Button
            type="button"
            onClick={() => void handleConfirmPrimaryTransfer()}
            disabled={!canConfirmTransfer}
          >
            {transferSubmitting ? 'Transferring...' : 'Confirm transfer'}
          </Button>
        </ModalActions>
      </Modal>

      <ModalBackdrop
        type="button"
        aria-label="Close delete organization modal"
        $open={deleteOrgModalOpen}
        onClick={() => {
          if (deleteOrgSubmitting) return;
          setDeleteOrgModalOpen(false);
        }}
      />
      <Modal
        $open={deleteOrgModalOpen}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-organization-title"
      >
        <ModalHead>
          <ModalTitle id="delete-organization-title">Delete organization</ModalTitle>
        </ModalHead>
        <ModalBody>
          <ModalText>
            This will permanently delete this business organization and remove all member associations.
          </ModalText>
          <ModalCard>
            <ModalLabel>Organization</ModalLabel>
            <ModalValue>
              {businessProfile?.businessName || businessProfile?.orgName || 'Business organization'}
            </ModalValue>
          </ModalCard>
          <ModalText>
            Only the primary account holder can complete this action.
          </ModalText>
        </ModalBody>
        <ModalActions>
          <QuietBtn
            type="button"
            onClick={() => setDeleteOrgModalOpen(false)}
            disabled={deleteOrgSubmitting}
          >
            Cancel
          </QuietBtn>
          <DangerButton
            type="button"
            onClick={() => void handleDeleteOrganization()}
            disabled={!businessProfile?.isPrimaryAccountHolder || deleteOrgSubmitting}
          >
            {deleteOrgSubmitting ? 'Deleting...' : 'Delete organization'}
          </DangerButton>
        </ModalActions>
      </Modal>

      <ModalBackdrop
        type="button"
        aria-label="Close leave organization modal"
        $open={leaveOrgModalOpen}
        onClick={() => {
          if (leaveOrgSubmitting) return;
          setLeaveOrgModalOpen(false);
          setLeaveOrgError(null);
        }}
      />
      <Modal
        $open={leaveOrgModalOpen}
        role="dialog"
        aria-modal="true"
        aria-labelledby="leave-organization-title"
      >
        <ModalHead>
          <ModalTitle id="leave-organization-title">Leave organization</ModalTitle>
        </ModalHead>
        <ModalBody>
          <ModalText>
            You will be removed from this business organization and lose access to its members, services, and workspace context.
          </ModalText>
          <ModalCard>
            <ModalLabel>Organization</ModalLabel>
            <ModalValue>
              {activeOrganization?.businessName
                || activeOrganization?.orgName
                || 'Organization'}
            </ModalValue>
          </ModalCard>
          <ModalCard>
            <ModalLabel>What happens next</ModalLabel>
            <ModalText>
              If you still belong to another organization, your workspace will switch to that organization automatically.
            </ModalText>
          </ModalCard>
          {leaveOrgError ? <ErrorText style={{ marginTop: 0 }}>{leaveOrgError}</ErrorText> : null}
        </ModalBody>
        <ModalActions>
          <QuietBtn
            type="button"
            onClick={() => {
              if (leaveOrgSubmitting) return;
              setLeaveOrgModalOpen(false);
              setLeaveOrgError(null);
            }}
          >
            Cancel
          </QuietBtn>
          <DangerGhostButton
            type="button"
            onClick={() => void handleLeaveOrganization()}
            disabled={leaveOrgSubmitting || businessProfile?.isPrimaryAccountHolder}
          >
            {leaveOrgSubmitting ? 'Leaving...' : 'Leave organization'}
          </DangerGhostButton>
        </ModalActions>
      </Modal>
    </Wrap>
  );
}
